import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Briefcase, Users, BarChart3, Plus, Edit2, Trash2, Eye,
  Loader2, CheckCircle, ArrowRight, Building2, TrendingUp,
  FileText, ChevronRight, X, AlertCircle, Search, Star, ChevronDown, Lock, Sparkles, CreditCard, Camera,
  Calendar, MapPin, ExternalLink, Clock, Video, Phone as PhoneIcon, Send, MessageSquare
} from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useAppShell } from "../context/AppShellContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { useJobNotifications } from "../context/JobNotificationsContext";
import { getAllPresetOptions, getPresetLabel, sanitizeCustomLabel, matchPresetFromLabel } from "../utils/companyVisibilityPresets";
import { LocationSelector } from "../components/LocationSelector";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { formatJobLocation } from "../utils/formatLocation";
import { JOB_CATEGORIES, OTHER_CATEGORY, JOB_TYPES, OTHER_JOB_TYPE, EXPERIENCE_LEVELS, OTHER_EXPERIENCE, JOB_TYPE_KEY_MAP, EXPERIENCE_KEY_MAP, jobTypeLabel, experienceLabel } from "../config/jobTaxonomy";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import CVPdfViewerModal from "../components/CVPdfViewerModal";
import EmployerCandidateView from "../components/EmployerCandidateView";
import NotifyApplicantModal from "../components/NotifyApplicantModal";
import OfferFormModal from "../components/OfferFormModal";
import InterviewFormModal from "../components/InterviewFormModal";
import { VerificationBadge } from "../components/VerificationBadge";
import { CompanyAvatar } from "../components/CompanyAvatar";
import { COMPANY_SIZE_OPTIONS, COMPANY_SIZE_LABEL_KEYS } from "../utils/companySize";
import { PrivacySettingsPanel } from "../components/PrivacySettingsPanel";
import { JobPreviewCard } from "../components/JobPreviewCard";
import JobMatchModal from "../components/JobMatchModal";
import { CandidateSummaryCard, CandidateActionButton, CandidateStageAction } from "../components/CandidateCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Multi-Country Foundation v1 — composes the legacy `location` string from the
// structured fields so old consumers (search, cards, map) keep working untouched.
const COUNTRY_NAMES_ES = { ES: "España", UY: "Uruguay", US: "Estados Unidos", AR: "Argentina", BR: "Brasil" };
const DEFAULT_CURRENCY_BY_COUNTRY = { ES: "EUR", UY: "UYU", US: "USD", AR: "ARS", BR: "BRL" };
const CURRENCIES = [
  { code: "EUR", symbol: "€" }, { code: "USD", symbol: "US$" },
  { code: "UYU", symbol: "$U" }, { code: "ARS", symbol: "AR$" }, { code: "BRL", symbol: "R$" },
];
const LOCATION_DEFAULTS = {
  country_code: null, admin1_code: null, admin1_name: null,
  admin2_code: null, admin2_name: null, city: null,
  latitude: null, longitude: null, location_verified: false,
};
function composeLocationString(f) {
  if (!f.city || !f.country_code) return "";
  const parts = [f.city];
  if (f.admin2_name && f.admin2_name !== f.city) parts.push(f.admin2_name);
  if (f.admin1_name) parts.push(f.admin1_name);
  parts.push(COUNTRY_NAMES_ES[f.country_code] || f.country_code);
  return parts.join(", ");
}

const PIPELINE_STAGES = [
  { key: "new_applicant", color: "#028CF8", bg: "bg-[#028CF8]/10", text: "text-[#028CF8]" },
  { key: "contacted", color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-700" },
  { key: "interview", color: "#8b5cf6", bg: "bg-purple-50", text: "text-purple-700" },
  { key: "offer", color: "#10b981", bg: "bg-green-50", text: "text-green-700" },
  { key: "hired", color: "#059669", bg: "bg-green-100", text: "text-green-800" },
  { key: "rejected", color: "#ef4444", bg: "bg-red-50", text: "text-red-700" },
];

// Stages where a real candidate-facing message makes sense — mirrors the
// backend's NOTIFIABLE_STAGES in server.py.
const NOTIFIABLE_STAGES = ["contacted", "interview", "offer", "hired", "rejected"];

function timeAgo(dateStr, t) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return t("jobCard.today");
  if (diff === 1) return t("jobCard.yesterday");
  return `${diff}${t("jobCard.dAgo")}`;
}

/* ── Hiring Probability Badge ──────────────────────────────────────────────── */
function HiringProbabilityBadge({ matchScore, t }) {
  if (!matchScore) return null;
  const config = matchScore >= 80
    ? { label: t("company.matchHigh"), bg: "rgba(16,185,129,0.1)", color: "#059669", icon: "▲" }
    : matchScore >= 60
    ? { label: t("company.matchMedium"), bg: "rgba(2,140,248,0.1)", color: "#028CF8", icon: "◆" }
    : { label: t("company.matchLow"), bg: "rgba(100,116,139,0.08)", color: "#64748B", icon: "▼" };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "11px", fontWeight: 700, padding: "3px 9px", borderRadius: "999px", backgroundColor: config.bg, color: config.color, fontFamily: "Poppins, sans-serif" }}>
      {config.icon} {config.label}
    </span>
  );
}

/* ── Próximas Entrevistas / Historial (Resumen tab) — reads directly from
   /company/dashboard's `interviews` payload (same interview_details already
   stored on the application doc, no second data model). ── */
function UpcomingInterviewsWidget({ interviews, t, onViewCandidate, onEditInterview }) {
  const [view, setView] = useState("upcoming"); // upcoming | history
  const list = interviews?.[view] || [];
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm" data-testid="upcoming-interviews-widget">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2.5">
        <div>
          <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("company.interviewsEyebrow")}</p>
          <h2 className="font-poppins text-xl font-bold text-slate-900">{t("company.upcomingInterviewsTitle")}</h2>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="interviews-view-toggle">
          <button onClick={() => setView("upcoming")} data-testid="interviews-view-upcoming"
            className={`px-3.5 min-h-[40px] rounded-md text-xs font-semibold transition-all ${view === "upcoming" ? "bg-white text-[#028CF8] shadow-sm" : "text-slate-500"}`}>
            {t("company.interviewsUpcomingTab")}
          </button>
          <button onClick={() => setView("history")} data-testid="interviews-view-history"
            className={`px-3.5 min-h-[40px] rounded-md text-xs font-semibold transition-all ${view === "history" ? "bg-white text-[#028CF8] shadow-sm" : "text-slate-500"}`}>
            {t("company.interviewsHistoryTab")}
          </button>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-sm text-slate-400 py-1" data-testid="upcoming-interviews-empty">
          {view === "upcoming" ? t("company.noUpcomingInterviews") : t("company.noInterviewHistory")}
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((app) => {
            const iv = app.interview_details || {};
            return (
              <CandidateSummaryCard
                key={app.application_id}
                cardTestId={`upcoming-interview-${app.application_id}`}
                avatarUrl={app.picture}
                name={app.name}
                line1={app.job_title}
                line2={
                  <span className="flex items-center gap-1.5 text-slate-500 whitespace-nowrap">
                    <Calendar className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    {iv.date}{iv.time ? ` · ${iv.time}` : ""}{iv.modality ? ` · ${t(`notifyApplicant.modality_${iv.modality}`)}` : ""}
                  </span>
                }
                profileAction={app.user_id ? { onClick: () => onViewCandidate(app), testId: `upcoming-interview-view-${app.application_id}`, label: t("company.candidateProfileAction") } : null}
                messageAction={app.user_id ? { onClick: () => onViewCandidate(app, true), testId: `upcoming-interview-message-${app.application_id}`, label: t("company.candidateMessageAction") } : null}
                thirdAction={view === "upcoming" ? (
                  <CandidateActionButton onClick={() => onEditInterview(app)} testId={`upcoming-interview-edit-${app.application_id}`} label={t("company.viewInterviewDetailsBtn")} />
                ) : null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Top Candidates Widget (Resumen tab) — cross-job summary, reuses the exact
   same /ranked scoring via a thin aggregate endpoint; no second algorithm. ── */
function TopCandidatesWidget({ API, t, onViewCv, onViewCandidate, setTab }) {
  const [candidates, setCandidates] = useState(null); // null = loading

  useEffect(() => {
    axios.get(`${API}/company/top-candidates`, { headers: getAuthHeaders() })
      .then((res) => setCandidates(res.data))
      .catch(() => setCandidates([]));
  }, [API]);

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm" data-testid="top-candidates-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("company.topCandidatesEyebrow")}</p>
          <h2 className="font-poppins text-xl font-bold text-slate-900">{t("company.topCandidatesTitle")}</h2>
        </div>
        {candidates?.length > 0 && (
          <button onClick={() => setTab("applicants")} className="text-sm text-[#028CF8] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
            {t("company.viewAll")} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {candidates === null ? (
        <div className="space-y-2.5">
          {[...Array(2)].map((_, i) => <div key={i} className="h-16 bg-slate-50 rounded-xl animate-pulse" />)}
        </div>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-slate-400 py-1" data-testid="top-candidates-empty">{t("company.noCandidatesYet")}</p>
      ) : (
        <div className="space-y-2">
          {candidates.map((app, i) => {
            const level = app.match_score >= 80
              ? { label: t("company.matchHigh"), color: "#059669" }
              : app.match_score >= 60
              ? { label: t("company.matchMedium"), color: "#028CF8" }
              : { label: t("company.matchLow"), color: "#64748B" };
            return (
              <CandidateSummaryCard
                key={app.application_id}
                cardTestId={`top-candidate-${i}`}
                avatarUrl={app.picture}
                name={app.name}
                line1={`${t("company.forJobLabel")} ${app.job_title}`}
                line2={
                  <span className="whitespace-nowrap" style={{ color: level.color }}>
                    {app.match_score}% {t("jobMatch.compatible")} · {level.label}
                  </span>
                }
                profileAction={app.user_id ? { onClick: () => onViewCandidate(app), testId: `top-candidate-view-profile-${i}`, label: t("company.candidateProfileAction") } : null}
                messageAction={app.user_id ? { onClick: () => onViewCandidate(app, true), testId: `top-candidate-message-${i}`, label: t("company.candidateMessageAction") } : null}
                thirdAction={(app.cv_type || app.cv_filename) ? (
                  <CandidateActionButton onClick={() => onViewCv(app)} testId={`top-candidate-view-cv-${i}`} label={t("company.viewCv")} />
                ) : null}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Top Candidates Section (ATS) ──────────────────────────────────────────── */
function TopCandidatesSection({ jobs, applications, updatingStage, updateStage, API, t, onViewCv, onViewCandidate, onEditInterview, onWithdrawOffer }) {
  const [selectedJobId, setSelectedJobId] = useState("all");
  const [rankedApps, setRankedApps] = useState(null);
  const [loadingRanked, setLoadingRanked] = useState(false);
  const [skillFilter, setSkillFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("all_stages");
  const [sortBy, setSortBy] = useState("recent"); // recent | match_desc | match_asc
  const [allScoresMap, setAllScoresMap] = useState({});
  const [matchAnalysis, setMatchAnalysis] = useState(null);
  const [matchAnalysisLoadingId, setMatchAnalysisLoadingId] = useState(null);

  const loadRanked = async (jobId) => {
    if (!jobId || jobId === "all") { setRankedApps(null); return; }
    setLoadingRanked(true);
    try {
      const res = await axios.get(`${API}/company/applications/${jobId}/ranked`, { headers: getAuthHeaders() });
      setRankedApps(res.data);
    } catch { setRankedApps(null); }
    finally { setLoadingRanked(false); }
  };

  const handleJobChange = (jid) => {
    setSelectedJobId(jid);
    if (jid !== "all") loadRanked(jid);
    else setRankedApps(null);
  };

  // "Todos los empleos" view has no single /ranked call to draw scores from — fetch each
  // job's ranked list once (same algorithm, same endpoint) and merge by application_id so
  // sorting by compatibility works across jobs too, without a second scoring implementation.
  useEffect(() => {
    if (selectedJobId !== "all") return;
    const jobIds = [...new Set(applications.map((a) => a.job_id))];
    if (jobIds.length === 0) { setAllScoresMap({}); return; }
    Promise.all(jobIds.map((jid) =>
      axios.get(`${API}/company/applications/${jid}/ranked`, { headers: getAuthHeaders() }).then((r) => r.data).catch(() => [])
    )).then((results) => {
      const map = {};
      results.flat().forEach((a) => { map[a.application_id] = a.match_score; });
      setAllScoresMap(map);
    });
  }, [selectedJobId, applications.length, API]);

  const openMatchAnalysis = async (app) => {
    setMatchAnalysisLoadingId(app.application_id);
    try {
      const res = await axios.get(`${API}/company/applications/${app.application_id}/match-analysis`, { headers: getAuthHeaders() });
      setMatchAnalysis({ ...res.data, __candidateName: app.name });
    } catch { /* silently ignore — button just won't open a modal */ }
    finally { setMatchAnalysisLoadingId(null); }
  };

  // Determine which apps to show — always overlay the latest stage from the
  // parent's `applications` state (source of truth for stage), since rankedApps
  // is a separate AI-ranked snapshot that only refreshes on job change.
  const stageByAppId = React.useMemo(() => {
    const m = {};
    applications.forEach((a) => { m[a.application_id] = a.stage; });
    return m;
  }, [applications]);

  let displayApps = rankedApps || applications;
  displayApps = displayApps.map((a) => ({
    ...a,
    stage: stageByAppId[a.application_id] !== undefined ? stageByAppId[a.application_id] : a.stage,
    match_score: selectedJobId === "all" ? (allScoresMap[a.application_id] ?? a.match_score) : a.match_score,
  }));

  // Apply filters
  if (skillFilter.trim()) {
    const kw = skillFilter.toLowerCase();
    displayApps = displayApps.filter((a) =>
      a.name?.toLowerCase().includes(kw) ||
      a.cover_letter?.toLowerCase().includes(kw) ||
      a.linkedin?.toLowerCase().includes(kw) ||
      a.email?.toLowerCase().includes(kw) ||
      a.cv_filename?.toLowerCase().includes(kw)
    );
  }
  if (selectedJobId !== "all" && !rankedApps) {
    displayApps = displayApps.filter((a) => a.job_id === selectedJobId);
  }

  // Pipeline quick-filter counts — computed before the stage filter itself is applied
  const stageCounts = { all_stages: displayApps.length };
  PIPELINE_STAGES.forEach((s) => { stageCounts[s.key] = displayApps.filter((a) => a.stage === s.key).length; });
  if (stageFilter !== "all_stages") displayApps = displayApps.filter((a) => a.stage === stageFilter);

  displayApps = [...displayApps].sort((a, b) => {
    if (sortBy === "match_desc") return (b.match_score ?? -1) - (a.match_score ?? -1);
    if (sortBy === "match_asc") return (a.match_score ?? 999) - (b.match_score ?? 999);
    return new Date(b.applied_date) - new Date(a.applied_date);
  });

  return (
    <div>
      <div className="mb-5">
        <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1 font-poppins">{t("cv.aiPowered")}</p>
        <h2 className="font-poppins text-2xl font-bold text-slate-900">
          {selectedJobId !== "all" ? t("company.topMatchingCandidates") : t("company.allApplicantsTitle")}
        </h2>
      </div>

      {/* Filter bar */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "20px", padding: "16px", background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        {/* Job selector */}
        <div className="w-full sm:w-auto">
          <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>
            {t("company.jobPositionLabel")}
          </label>
          <select
            data-testid="job-filter-select"
            value={selectedJobId}
            onChange={(e) => handleJobChange(e.target.value)}
            className="w-full sm:w-[200px]"
            style={{ height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#0F172A", background: "white", outline: "none", cursor: "pointer" }}
          >
            <option value="all">{t("company.allJobsOption")}</option>
            {jobs.filter((j) => j.status === "active").map((j) => (
              <option key={j.job_id} value={j.job_id}>{j.title}</option>
            ))}
          </select>
        </div>

        {/* Skill/text search */}
        <div className="w-full sm:w-auto">
          <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>
            {t("company.searchCandidatesLabel")}
          </label>
          <div style={{ position: "relative" }}>
            <Search style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "#94A3B8" }} />
            <input
              type="text"
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              placeholder={t("company.searchCandidatesPlaceholder")}
              className="w-full sm:w-[240px]"
              style={{ height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 12px 0 30px", fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#0F172A", outline: "none", boxSizing: "border-box" }}
            />
            {skillFilter && (
              <button onClick={() => setSkillFilter("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}>
                <X style={{ width: 13, height: 13 }} />
              </button>
            )}
          </div>
        </div>

        {selectedJobId !== "all" && (
          <div style={{ marginLeft: "auto", alignSelf: "flex-end" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 600, color: "#028CF8", background: "rgba(2,140,248,0.08)", padding: "6px 12px", borderRadius: "999px", fontFamily: "Poppins, sans-serif" }}>
              <Star style={{ width: 12, height: 12 }} /> {t("company.rankedByAI")}
            </div>
          </div>
        )}

        {/* Sort control */}
        <div className="w-full sm:w-auto sm:ml-auto">
          <label style={{ display: "block", fontSize: "11px", fontWeight: 600, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px", fontFamily: "Poppins, sans-serif" }}>
            {t("company.sortByLabel")}
          </label>
          <select
            data-testid="applicants-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="w-full sm:w-[190px]"
            style={{ height: "38px", border: "1px solid #E5E7EB", borderRadius: "9px", padding: "0 10px", fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#0F172A", background: "white", outline: "none", cursor: "pointer" }}
          >
            <option value="recent">{t("company.sortRecent")}</option>
            <option value="match_desc">{t("company.sortMatchDesc")}</option>
            <option value="match_asc">{t("company.sortMatchAsc")}</option>
          </select>
        </div>
      </div>

      {/* Pipeline quick filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-4" data-testid="applicants-stage-filters">
        {[{ key: "all_stages", labelKey: "company.stageFilterAll" }, ...PIPELINE_STAGES.map((s) => ({ key: s.key, labelKey: `pipeline.${s.key}` }))].map((f) => (
          <button
            key={f.key}
            onClick={() => setStageFilter(f.key)}
            data-testid={`stage-filter-${f.key}`}
            className={`flex-shrink-0 px-3.5 min-h-[38px] rounded-full text-xs font-semibold whitespace-nowrap transition-all border ${stageFilter === f.key ? "bg-[#028CF8] text-white border-[#028CF8]" : "bg-white text-slate-600 border-[#E5E7EB] hover:border-[#028CF8]/40"}`}
          >
            {t(f.labelKey)} <span className={stageFilter === f.key ? "opacity-80" : "text-slate-400"}>({stageCounts[f.key] || 0})</span>
          </button>
        ))}
      </div>

      {/* Candidate list */}
      {loadingRanked ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <Loader2 style={{ width: 24, height: 24, color: "#028CF8", animation: "spin 1s linear infinite" }} />
        </div>
      ) : displayApps.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("company.noApplicants")}</h3>
          <p className="text-slate-500 text-sm">{t("company.appsAppearHere")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayApps.map((app, idx) => {
            const stage = PIPELINE_STAGES.find((s) => s.key === app.stage);
            const hasScore = app.match_score != null;
            return (
              <div key={app.application_id} data-testid={`applicant-${app.application_id}`}
                style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "16px", padding: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", transition: "all 0.2s" }}
                className="hover:shadow-md hover:-translate-y-0.5 hover:border-[#028CF8]/20">

                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Rank number (when ranked) */}
                  {hasScore && selectedJobId !== "all" && (
                    <div style={{ width: 36, height: 36, borderRadius: "10px", background: idx === 0 ? "#028CF8" : idx === 1 ? "rgba(2,140,248,0.12)" : "rgba(100,116,139,0.08)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: "14px", fontWeight: 800, color: idx === 0 ? "white" : "#028CF8", fontFamily: "Poppins, sans-serif" }}>#{idx + 1}</span>
                    </div>
                  )}

                  {/* Avatar */}
                  <div style={{ width: 42, height: 42, borderRadius: "12px", background: "rgba(2,140,248,0.1)", border: "1px solid rgba(2,140,248,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Poppins, sans-serif", fontWeight: 700, color: "#028CF8", fontSize: "16px", flexShrink: 0, overflow: "hidden" }}>
                    {app.picture ? <img src={app.picture} alt={app.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : app.name?.[0]?.toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                      <h3 style={{ fontSize: "15px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif" }}>{app.name}</h3>
                      {/* Match score */}
                      {hasScore && (
                        <span style={{
                          fontSize: "11px", fontWeight: 700, padding: "2px 9px", borderRadius: "999px", fontFamily: "Poppins, sans-serif",
                          background: app.match_score >= 80 ? "rgba(16,185,129,0.1)" : app.match_score >= 65 ? "rgba(2,140,248,0.1)" : "rgba(100,116,139,0.08)",
                          color: app.match_score >= 80 ? "#059669" : app.match_score >= 65 ? "#028CF8" : "#64748B",
                        }}>
                          {app.match_score >= 80 ? "★" : "●"} {app.match_score}% {t("jobMatch.compatible")}
                        </span>
                      )}
                      {hasScore && (
                        <button onClick={() => openMatchAnalysis(app)} data-testid={`view-match-analysis-${app.application_id}`}
                          disabled={matchAnalysisLoadingId === app.application_id}
                          style={{ fontSize: "11px", fontWeight: 600, color: "#94A3B8", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                          {matchAnalysisLoadingId === app.application_id ? "…" : t("company.viewMatchAnalysisBtn")}
                        </button>
                      )}
                      {/* Hiring Probability */}
                      {hasScore && <HiringProbabilityBadge matchScore={app.match_score} t={t} />}
                    </div>
                    <p style={{ fontSize: "12px", color: "#64748B", fontFamily: "Poppins, sans-serif", marginBottom: "2px" }}>{app.email} · {app.phone}</p>
                    <p style={{ fontSize: "12px", color: "#028CF8", fontWeight: 600, fontFamily: "Poppins, sans-serif", marginBottom: "2px" }}>{app.job_title}</p>
                    {app.cv_type && (
                      <p style={{ fontSize: "11px", fontWeight: 600, fontFamily: "Poppins, sans-serif", color: app.cv_type === "adapted" ? "#F55FA5" : "#64748B", marginBottom: "2px" }} data-testid={`cv-type-label-${app.application_id}`}>
                        {app.cv_type === "adapted" ? `${t("company.adaptedCvSubmitted")}: ${app.cv_version_name}` : t("company.primaryCvSubmitted")}
                      </p>
                    )}
                    {app.linkedin && (
                      <a href={app.linkedin} target="_blank" rel="noopener noreferrer" style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif" }}>
                        {t("company.linkedinProfileLink")}
                      </a>
                    )}
                    <p style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif", marginTop: "2px" }}>{t("userDash.applied")} {timeAgo(app.applied_date, t)}</p>
                  </div>

                  {/* Actions */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 }}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      {app.cv_type && (
                        <button onClick={() => onViewCv(app)}
                          data-testid={`view-cv-${app.application_id}`}
                          style={{ display: "flex", alignItems: "center", gap: "5px", height: "34px", padding: "0 12px", border: "1px solid rgba(2,140,248,0.3)", color: "#028CF8", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          <FileText style={{ width: 13, height: 13 }} /> {t("company.viewCv")}
                        </button>
                      )}
                      {app.cv_filename && !app.cv_type && (
                        <button onClick={() => onViewCv(app)}
                          data-testid={`view-cv-${app.application_id}`}
                          style={{ display: "flex", alignItems: "center", gap: "5px", height: "34px", padding: "0 12px", border: "1px solid rgba(2,140,248,0.3)", color: "#028CF8", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          <FileText style={{ width: 13, height: 13 }} /> {t("company.viewCv")}
                        </button>
                      )}
                      {app.user_id && (
                        <button onClick={() => onViewCandidate(app)}
                          data-testid={`view-profile-${app.application_id}`}
                          style={{ display: "flex", alignItems: "center", gap: "5px", height: "34px", padding: "0 12px", border: "1px solid rgba(2,140,248,0.3)", color: "#028CF8", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          <Eye style={{ width: 13, height: 13 }} /> {t("company.profileLink")}
                        </button>
                      )}
                      {app.user_id && (
                        <button onClick={() => onViewCandidate(app, true)}
                          data-testid={`send-message-${app.application_id}`}
                          style={{ display: "flex", alignItems: "center", gap: "5px", height: "34px", padding: "0 12px", border: "1px solid rgba(2,140,248,0.3)", color: "#028CF8", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          <Send style={{ width: 13, height: 13 }} /> {t("messages.sendMessage")}
                        </button>
                      )}
                      {!app.cv_type && app.has_cv === false && !app.cv_filename && (
                        <span style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif", alignSelf: "center" }}>{t("company.noCvUploaded")}</span>
                      )}
                    </div>
                    <select
                      data-testid={`stage-select-${app.application_id}`}
                      value={app.stage}
                      onChange={(e) => updateStage(app, e.target.value)}
                      disabled={updatingStage === app.application_id}
                      style={{ height: "34px", border: "none", borderRadius: "8px", padding: "0 8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", cursor: "pointer", outline: "none", backgroundColor: stage?.bg?.replace("bg-", "") || "#F8FAFC" }}
                      className={stage ? `${stage.bg} ${stage.text}` : "bg-slate-50 text-slate-600"}
                    >
                      {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{t(`pipeline.${s.key}`)}</option>)}
                    </select>
                  </div>
                </div>

                {app.cover_letter && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #F1F5F9" }}>
                    <p style={{ fontSize: "11px", fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "4px", fontFamily: "Poppins, sans-serif" }}>{t("jobDetailExtra.coverLetter")}</p>
                    <p style={{ fontSize: "12px", color: "#64748B", lineHeight: "1.6", fontFamily: "Poppins, sans-serif", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{app.cover_letter}</p>
                  </div>
                )}

                {app.stage === "interview" && app.interview_details && Object.keys(app.interview_details).length > 0 && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #F1F5F9" }} className="relative z-[51]" data-testid={`interview-scheduled-${app.application_id}`}>
                    <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "8px", fontFamily: "Poppins, sans-serif" }}>
                      {t("company.interviewScheduledTitle")}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" style={{ fontFamily: "Poppins, sans-serif" }}>
                      {app.interview_details.date && (
                        <p className="flex items-center gap-1.5" style={{ fontSize: "12px", color: "#475569" }}>
                          <Calendar className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {app.interview_details.date}
                          {app.interview_details.time ? ` · ${app.interview_details.time}` : ""}
                          {app.interview_details.end_time ? `–${app.interview_details.end_time}` : ""}
                        </p>
                      )}
                      {app.interview_details.modality && (
                        <p className="flex items-center gap-1.5" style={{ fontSize: "12px", color: "#475569" }}>
                          {app.interview_details.modality === "video" ? <Video className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /> : app.interview_details.modality === "phone" ? <PhoneIcon className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /> : <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
                          {t(`notifyApplicant.modality_${app.interview_details.modality}`)}
                        </p>
                      )}
                      {app.interview_details.modality === "in_person" && app.interview_details.location && (
                        <p className="flex items-center gap-1.5 truncate" style={{ fontSize: "12px", color: "#475569" }}>
                          <MapPin className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" /> <span className="truncate">{app.interview_details.location}</span>
                        </p>
                      )}
                      {app.interview_details.modality === "video" && app.interview_details.link && (
                        <p className="flex items-center gap-1.5 truncate" style={{ fontSize: "12px", color: "#028CF8" }}>
                          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                          <a href={app.interview_details.link} target="_blank" rel="noopener noreferrer" className="truncate hover:underline">{app.interview_details.link}</a>
                        </p>
                      )}
                      {app.interview_details.contact_name && (
                        <p className="flex items-center gap-1.5" style={{ fontSize: "12px", color: "#475569" }}>
                          <Users className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {app.interview_details.contact_name}{app.interview_details.contact_role ? ` — ${app.interview_details.contact_role}` : ""}
                        </p>
                      )}
                    </div>
                    <button onClick={() => onEditInterview(app)} data-testid={`edit-interview-${app.application_id}`}
                      style={{ marginTop: "10px", height: "32px", padding: "0 12px", border: "1px solid rgba(2,140,248,0.3)", color: "#028CF8", borderRadius: "8px", fontSize: "12px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                      {t("company.editInterviewBtn")}
                    </button>
                  </div>
                )}

                {app.stage === "offer" && app.offer && (
                  <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid #F1F5F9" }} data-testid={`offer-status-${app.application_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        app.offer.status === "pending" ? "bg-amber-50 text-amber-600" :
                        app.offer.status === "accepted" ? "bg-emerald-50 text-emerald-600" :
                        app.offer.status === "rejected" ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-500"
                      }`}>
                        {t(`company.offerStatus_${app.offer.status}`)}
                      </span>
                      {app.offer.status === "pending" && (
                        <button onClick={() => onWithdrawOffer(app)} data-testid={`withdraw-offer-${app.application_id}`}
                          style={{ height: "28px", padding: "0 10px", border: "1px solid #FCA5A5", color: "#DC2626", borderRadius: "8px", fontSize: "11px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          {t("company.offerWithdrawBtn")}
                        </button>
                      )}
                      {app.offer.status === "accepted" && (
                        <button
                          onClick={() => {
                            if (window.confirm(t("company.confirmHireMsg").replace("{name}", app.name).replace("{job}", app.job_title))) {
                              updateStage(app, "hired");
                            }
                          }}
                          data-testid={`confirm-hire-${app.application_id}`}
                          style={{ height: "28px", padding: "0 10px", border: "1px solid #A7F3D0", color: "#059669", borderRadius: "8px", fontSize: "11px", fontWeight: 600, fontFamily: "Poppins, sans-serif", background: "none", cursor: "pointer" }}>
                          {t("company.confirmHireBtn")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {matchAnalysis && (
        <JobMatchModal
          analysis={matchAnalysis}
          onClose={() => setMatchAnalysis(null)}
          viewerRole="employer"
          candidateName={matchAnalysis.__candidateName}
        />
      )}
    </div>
  );
}

export default function CompanyDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setEmployerNav } = useAppShell();
  const { unreadCount } = useUnreadMessages();
  const { unreadCount: jobUnreadCount, subscribeToJobNotifications, markApplicationsRead } = useJobNotifications();

  // Nav items use t() — must be inside component. "messages" navigates to the
  // real /messages route (not a local tab) — Mensajes now lives here as the
  // PRIMARY entry point, no longer duplicated in the header.
  const NAV_ITEMS = [
    { id: "overview",   label: t("tabs.overview"),       icon: BarChart3 },
    { id: "post",       label: t("tabs.postJob"),        icon: Plus },
    { id: "jobs",       label: t("tabs.myListings"),     icon: Briefcase },
    { id: "applicants", label: t("tabs.applicants"),     icon: Users },
    { id: "messages",   label: t("bottomNav.messages"),  icon: MessageSquare },
    { id: "company",    label: t("tabs.companyProfile"), icon: Building2 },
  ];
  // Lazy-initialize tab and jobForm from URL prefill params
  // Supports: ?tab=post&prefill_title=Developer&prefill_city=Miami
  const [tab, setTabState] = useState(() => {
    const t = searchParams.get("tab");
    const hasPrefill = searchParams.get("prefill_title") || searchParams.get("prefill_city");
    return (hasPrefill || t === "post") ? "post" : (t || "overview");
  });
  // Keeps the URL (?tab=) in sync so the tab is shareable/restorable, while
  // remaining the single owner of the actual tab state.
  const setTab = (id) => { setTabState(id); setSearchParams({ tab: id }, { replace: true }); };
  // Switching tabs never navigates route (same /company/dashboard URL), so
  // ScrollToTop (App.js, keyed on pathname) never fires here — without this,
  // any tab change while scrolled down (Sidebar, BottomNav, or a redirect
  // like handlePublishJob's setTab("jobs")) lands the new tab wherever the
  // previous one was scrolled, including all the way at the bottom.
  useEffect(() => { window.scrollTo(0, 0); }, [tab]);

  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStage, setUpdatingStage] = useState(null);
  const [editingJobId, setEditingJobId] = useState(null);
  // Mis Anuncios UX (Fase 3) — búsqueda/filtro/orden client-side, sin tocar
  // la carga de datos existente (jobs ya vienen completos del /company/jobs).
  const [jobsSearch, setJobsSearch] = useState("");
  const [jobsStatusFilter, setJobsStatusFilter] = useState("all");
  const [jobsSort, setJobsSort] = useState("newest");
  const [viewingCv, setViewingCv] = useState(null);
  const [viewingCandidate, setViewingCandidate] = useState(null);
  const openCandidate = (app, openComposer = false) => setViewingCandidate({ ...app, __openComposer: !!openComposer });
  const [notifyCtx, setNotifyCtx] = useState(null);
  const [offerModalCtx, setOfferModalCtx] = useState(null);
  const [interviewModalCtx, setInterviewModalCtx] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  // Publish access gate — reuses the existing Plans/Trial/Company Access system
  // (Admin V2), no Stripe involved yet. `accessStatus.has_access` is the single
  // source of truth the backend also checks before allowing a job to go active.
  const [accessStatus, setAccessStatus] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessGateReason, setAccessGateReason] = useState("access_required");
  const [publicPlans, setPublicPlans] = useState(null);
  const [activatingPlanId, setActivatingPlanId] = useState(null);

  // Full re-fetch (not a partial merge) so listings_limit/listings_used/listings_remaining
  // — which live at the top level of the response, not inside `access` — never go stale
  // after an activation (previously only {has_access, access} were patched in-place).
  const refreshAccessStatus = async () => {
    try {
      const res = await axios.get(`${API}/company/access`, { headers: getAuthHeaders() });
      setAccessStatus(res.data);
    } catch { /* keep last known status */ }
  };

  const openAccessModal = async () => {
    setShowAccessModal(true);
    if (publicPlans === null) {
      try {
        const res = await axios.get(`${API}/plans/public`, { headers: getAuthHeaders() });
        setPublicPlans(res.data || []);
      } catch { setPublicPlans([]); }
    }
    await refreshAccessStatus();
  };

  const handleAccessRequired = (err) => {
    if (err?.response?.status === 402) {
      setAccessGateReason(err.response?.data?.detail?.code || "access_required");
      openAccessModal();
      return true;
    }
    return false;
  };

  const activatePlan = async (planId) => {
    setActivatingPlanId(planId);
    try {
      await axios.post(`${API}/company/access/select-plan`, { plan_id: planId }, { headers: getAuthHeaders() });
      await refreshAccessStatus();
      setShowAccessModal(false);
      showToast(t("companyAccess.activatedToast"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      showToast((typeof detail === "object" ? detail?.message : detail) || t("company.postErrorGeneric"));
    }
    finally { setActivatingPlanId(null); }
  };

  // Paid plans NEVER activate access directly — this only opens a Stripe Checkout
  // Session; access is granted exclusively by the signed webhook once Stripe
  // confirms payment (see backend `/stripe/webhook`), never by this redirect.
  const checkoutForPlan = async (planId) => {
    setActivatingPlanId(planId);
    try {
      const res = await axios.post(`${API}/company/billing/checkout`, { plan_id: planId }, { headers: getAuthHeaders() });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      showToast((typeof detail === "object" ? detail?.message : detail) || t("company.postErrorGeneric"));
      setActivatingPlanId(null);
    }
  };

  const [portalLoading, setPortalLoading] = useState(false);
  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await axios.post(`${API}/company/billing/portal`, {}, { headers: getAuthHeaders() });
      window.location.href = res.data.url;
    } catch {
      showToast(t("companyBilling.portalError"));
      setPortalLoading(false);
    }
  };

  const startTrial = async (planId) => {
    setActivatingPlanId(planId);
    try {
      await axios.post(`${API}/company/access/start-trial`, { plan_id: planId }, { headers: getAuthHeaders() });
      await refreshAccessStatus();
      setShowAccessModal(false);
      showToast(t("companyAccess.trialStartedToast"));
    } catch (err) { showToast(err.response?.data?.detail || t("company.postErrorGeneric")); }
    finally { setActivatingPlanId(null); }
  };

  const EMPTY_JOB_FORM = {
    title: "", location: "", job_type: "full-time", experience_level: "mid",
    category: "Technology", salary_min: "", salary_max: "", salary_currency: "USD",
    salary_period: "year", salary_visible: true,
    work_mode: "onsite", remote_countries: [],
    description: "", requirements: [""], benefits: [""],
    application_method: "internal", external_apply_link: "",
    urgency: "normal", shift_label: "",
    company_visibility: "public", company_display_preset: "important", company_display_custom: "",
    address: "",
    ...LOCATION_DEFAULTS,
  };

  const [jobForm, setJobForm] = useState(() => ({
    title:    searchParams.get("prefill_title")   || "",
    location: searchParams.get("prefill_city")    || "",
    job_type: "full-time", experience_level: "mid",
    category: "Technology", salary_min: "", salary_max: "", salary_currency: "USD",
    salary_period: "year", salary_visible: true,
    work_mode: "onsite", remote_countries: [],
    description: "", requirements: [""], benefits: [""],
    application_method: "internal", external_apply_link: "",
    urgency: "normal", // normal | urgent | immediate — UI-only, translated into real fields on submit
    shift_label: "",
    company_visibility: "public", company_display_preset: "important", company_display_custom: "",
    address: "",
    ...LOCATION_DEFAULTS,
  }));
  const [postingJob, setPostingJob] = useState(false);
  const [postError, setPostError] = useState("");
  const [postSuccess, setPostSuccess] = useState(false);
  const [currencyManual, setCurrencyManual] = useState(false);
  const [locationTouched, setLocationTouched] = useState(false);
  const [postStep, setPostStep] = useState("form"); // "form" | "preview" — Vista previa antes de publicar
  const [improvingDescription, setImprovingDescription] = useState(false);
  const [aiDescriptionProposal, setAiDescriptionProposal] = useState(null);

  const startNewJob = async () => {
    // Access must be checked BEFORE showing the form — refetch fresh (don't trust
    // possibly-stale mount-time accessStatus) so the paywall/limit modal opens
    // immediately instead of after the employer fills out an entire listing.
    let status = accessStatus;
    try {
      const res = await axios.get(`${API}/company/access`, { headers: getAuthHeaders() });
      status = res.data;
      setAccessStatus(res.data);
    } catch { /* fall back to last known status below */ }
    if (!status?.has_access) { setAccessGateReason("access_required"); openAccessModal(); return; }
    if (status.listings_limit != null && status.listings_remaining <= 0) { setAccessGateReason("listing_limit_reached"); openAccessModal(); return; }
    setEditingJobId(null); setJobForm(EMPTY_JOB_FORM); setCurrencyManual(false); setLocationTouched(false); setTab("post"); setPostStep("form"); setAiDescriptionProposal(null);
    window.scrollTo(0, 0);
  };

  // Hand the current tab + the "Publicar" access-gated handler off to the
  // global BottomNav (via AppShellContext) so it can render this page's 5
  // tabs without a second, duplicated bottom nav living inside this file.
  useEffect(() => {
    setEmployerNav({ tab, setTab, startNewJob });
    return () => setEmployerNav(null);
  }, [tab, accessStatus]);

  const handleLocationChange = (patch) => {
    setLocationTouched(true);
    setJobForm((f) => {
      const next = { ...f, ...patch };
      if (patch.country_code && !currencyManual) {
        next.salary_currency = DEFAULT_CURRENCY_BY_COUNTRY[patch.country_code] || f.salary_currency;
      }
      return next;
    });
  };

  // Keep the legacy `location` string in sync with the structured fields so
  // search/cards/map (which still read `location`) never see stale data. If
  // the job has no structured city yet (untouched legacy job), this is a
  // no-op and the original `location` string is preserved as-is.
  useEffect(() => {
    const composed = composeLocationString(jobForm);
    if (composed && composed !== jobForm.location) {
      setJobForm((f) => ({ ...f, location: composed }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobForm.city, jobForm.admin1_name, jobForm.admin2_name, jobForm.country_code]);

  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
    if (!authLoading && user?.role !== "employer") navigate("/dashboard");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      setLoading(true);
      try {
        const [statsRes, jobsRes, appsRes, companyRes, accessRes] = await Promise.all([
          axios.get(`${API}/company/dashboard`, { headers: getAuthHeaders() }),
          axios.get(`${API}/company/jobs`, { headers: getAuthHeaders() }),
          axios.get(`${API}/company/applications`, { headers: getAuthHeaders() }),
          axios.get(`${API}/my/company`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
          axios.get(`${API}/company/access`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        ]);
        setStats(statsRes.data);
        setJobs(jobsRes.data || []);
        setApplications(appsRes.data || []);
        if (companyRes.data) { setCompany(companyRes.data); setCompanyForm(companyRes.data); }
        if (accessRes.data) setAccessStatus(accessRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [user]);

  // Deep-link support: AI chat "Ver perfil" cards navigate to
  // ?tab=applicants&open_application=<id> so the exact candidate opens automatically.
  // Also reused by job-notification deep links (nuevo postulante, oferta
  // aceptada/rechazada) — opening the candidate here IS "visualizar el aviso".
  useEffect(() => {
    const openId = searchParams.get("open_application");
    if (!openId || applications.length === 0) return;
    const found = applications.find((a) => a.application_id === openId);
    if (found) {
      setTab("applicants");
      setViewingCandidate(found);
      markApplicationsRead([openId]);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open_application");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications.length]);

  // Realtime: cuando el candidato acepta/rechaza una oferta (u otro evento
  // laboral llega por WS), se parchea directo el estado en memoria — la
  // MISMA fuente de verdad que ya usa el resto de la pantalla (app.offer),
  // sin refetch manual ni segunda fuente de datos.
  useEffect(() => {
    const unsubscribe = subscribeToJobNotifications((notification) => {
      if (!notification) return;
      const { type, related_application_id, metadata } = notification;
      if (!related_application_id) return;
      if (type === "offer_status_update") {
        setApplications((prev) => prev.map((a) => (
          a.application_id === related_application_id && a.offer
            ? { ...a, offer: { ...a.offer, status: metadata?.status || a.offer.status } }
            : a
        )));
      } else if (type === "offer_withdrawn") {
        setApplications((prev) => prev.map((a) => (
          a.application_id === related_application_id && a.offer
            ? { ...a, offer: { ...a.offer, status: "withdrawn" } }
            : a
        )));
      }
    });
    return unsubscribe;
  }, [subscribeToJobNotifications]);

  // Entrar a "Candidatos" y ver la lista ES visualizar esos avisos — nunca se
  // marca como leído solo por abrir la app, solo al entrar a esta pantalla.
  useEffect(() => {
    if (tab !== "applicants" || applications.length === 0) return;
    markApplicationsRead(applications.map((a) => a.application_id));
  }, [tab, applications.length, markApplicationsRead]);

  // Stripe redirects here after Checkout — this ONLY shows a toast + re-polls
  // `/company/access`; it never grants access itself (the webhook already did,
  // or will within a couple seconds — hence the follow-up poll below).
  useEffect(() => {
    const billing = searchParams.get("billing");
    if (!billing) return;
    if (billing === "success") {
      showToast(t("companyBilling.paymentSuccessToast"));
      refreshAccessStatus();
      setTimeout(refreshAccessStatus, 2500);
    } else if (billing === "cancelled") {
      showToast(t("companyBilling.paymentCancelledToast"));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("billing"); next.delete("session_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateJobForm = () => {
    if (jobForm.salary_min && jobForm.salary_max && Number(jobForm.salary_min) > Number(jobForm.salary_max)) {
      return t("company.salaryRangeError");
    }
    if (jobForm.company_visibility === "hidden" && jobForm.company_display_preset === "other" && !sanitizeCustomLabel(jobForm.company_display_custom)) {
      return t("companyVisibility.presetOtherPlaceholder");
    }
    if (!editingJobId && jobForm.work_mode !== "remote" && (!jobForm.country_code || !jobForm.city)) {
      return t("company.locationRequiredError");
    }
    if (jobForm.application_method === "external" && !/^https?:\/\/.+/i.test(jobForm.external_apply_link || "")) {
      return t("postJobLabels.externalLinkInvalid");
    }
    return "";
  };

  const handleShowPreview = (e) => {
    e.preventDefault();
    const err = validateJobForm();
    if (err) { setPostError(err); return; }
    setPostError("");
    setPostStep("preview");
    window.scrollTo(0, 0);
  };

  const buildJobPayload = () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const { urgency, company_display_preset, company_display_custom, application_method, ...rest } = jobForm;
    const displayLabel = jobForm.company_visibility === "hidden"
      ? (company_display_preset === "other"
          ? sanitizeCustomLabel(company_display_custom)
          : getPresetLabel(company_display_preset, jobForm.category, t))
      : null;
    return {
      ...rest,
      company_display_label: displayLabel,
      salary_min: jobForm.salary_min ? Number(jobForm.salary_min) : null,
      salary_max: jobForm.salary_max ? Number(jobForm.salary_max) : null,
      requirements: jobForm.requirements.filter(Boolean),
      benefits: jobForm.benefits.filter(Boolean),
      // "" (not null) so an edit that switches back to internal actually clears a previous external link
      external_apply_link: application_method === "external" ? jobForm.external_apply_link : "",
      shift_label: jobForm.shift_label || null,
      remote: jobForm.work_mode === "remote",
      remote_countries: jobForm.work_mode === "remote" ? jobForm.remote_countries : [],
      // Exact address only makes sense for on-site jobs — dropped otherwise (mirrors
      // how remote_countries above only applies to work_mode === "remote").
      address: jobForm.work_mode === "onsite" ? (jobForm.address || null) : null,
      is_urgent: urgency === "urgent",
      start_date: urgency === "immediate" ? todayStr : null,
      urgency_label: urgency === "immediate" ? "INMEDIATA" : urgency === "urgent" ? "URGENTE" : null,
    };
  };

  const handlePublishJob = async () => {
    setPostingJob(true);
    setPostError("");
    try {
      const payload = buildJobPayload();
      if (editingJobId) {
        const res = await axios.put(`${API}/jobs/${editingJobId}`, payload, { headers: getAuthHeaders() });
        setJobs((prev) => prev.map((j) => j.job_id === editingJobId ? res.data : j));
        showToast(t("company.jobUpdatedOk"));
        if (locationTouched && res.data.location_verified === false) showToast(t("company.locationNotVerifiedWarning"));
        setEditingJobId(null);
        setJobForm(EMPTY_JOB_FORM);
        setCurrencyManual(false);
        setLocationTouched(false);
        setPostStep("form");
        setTab("jobs");
      } else {
        const res = await axios.post(`${API}/jobs`, payload, { headers: getAuthHeaders() });
        setJobs((prev) => [res.data, ...prev]);
        setPostSuccess(true);
        setJobForm(EMPTY_JOB_FORM);
        setCurrencyManual(false);
        setPostStep("form");
        if (stats) setStats((s) => ({ ...s, total_jobs: s.total_jobs + 1 }));
        if (res.data.location_verified === false) showToast(t("company.locationNotVerifiedWarning"));
        setTimeout(() => { setPostSuccess(false); setTab("jobs"); }, 1500);
      }
    } catch (err) {
      setPostStep("form");
      if (!handleAccessRequired(err)) setPostError(err.response?.data?.detail || t("company.postErrorGeneric"));
    }
    finally { setPostingJob(false); }
  };

  const handleImproveDescriptionWithAI = async () => {
    if (!jobForm.description.trim()) { setPostError(t("postJobLabels.aiImproveEmptyError")); return; }
    setImprovingDescription(true);
    setPostError("");
    try {
      const res = await axios.post(`${API}/ai/improve-job-description`, { description: jobForm.description }, { headers: getAuthHeaders() });
      setAiDescriptionProposal(res.data.improved_description);
    } catch {
      setPostError(t("postJobLabels.aiImproveError"));
    }
    finally { setImprovingDescription(false); }
  };

  const startEditJob = (job) => {
    setEditingJobId(job.job_id);
    const { preset, custom } = job.company_visibility === "hidden"
      ? matchPresetFromLabel(job.company_display_label, job.category || "Technology", t)
      : { preset: "important", custom: "" };
    setCurrencyManual(true); // job already has an explicit currency — don't auto-override on country change
    setLocationTouched(false);
    setPostStep("form");
    setJobForm({
      title: job.title || "", location: job.location || "",
      job_type: job.job_type || "full-time", experience_level: job.experience_level || "mid",
      category: job.category || "Technology",
      salary_min: job.salary_min ?? "", salary_max: job.salary_max ?? "",
      salary_currency: job.salary_currency || "USD",
      salary_period: job.salary_period || "year",
      salary_visible: job.salary_visible !== false,
      work_mode: job.work_mode || (job.remote ? "remote" : "onsite"),
      remote_countries: job.remote_countries || [],
      description: job.description || "",
      requirements: job.requirements?.length ? job.requirements : [""],
      benefits: job.benefits?.length ? job.benefits : [""],
      application_method: job.external_apply_link ? "external" : "internal",
      external_apply_link: job.external_apply_link || "",
      // Legacy "today"/"HOY" jobs map into the closest new bucket: immediate start
      urgency: job.is_urgent ? "urgent" : job.start_date ? "immediate" : "normal",
      shift_label: job.shift_label || "",
      company_visibility: job.company_visibility === "hidden" ? "hidden" : "public",
      company_display_preset: preset, company_display_custom: custom,
      country_code: job.country_code || null, admin1_code: job.admin1_code || null,
      admin1_name: job.admin1_name || null, admin2_code: job.admin2_code || null,
      admin2_name: job.admin2_name || null, city: job.city || null,
      latitude: job.latitude ?? null, longitude: job.longitude ?? null,
      location_verified: !!job.location_verified,
      address: job.address || "",
    });
    setPostError("");
    setTab("post");
  };

  const cancelEditJob = () => {
    setEditingJobId(null);
    setJobForm(EMPTY_JOB_FORM);
    setCurrencyManual(false);
    setLocationTouched(false);
    setPostError("");
    setPostStep("form");
    setAiDescriptionProposal(null);
    setTab("jobs");
  };

  const updateStage = async (app, stage) => {
    const appId = app.application_id;
    const previous = applications;
    const prevStage = previous.find((a) => a.application_id === appId)?.stage;
    // "Oferta" never patches the stage directly — it opens the offer form;
    // the stage only changes once the employer actually sends a real offer
    // (POST /applications/{id}/offer, wired in OfferFormModal's onSent).
    if (stage === "offer") {
      setOfferModalCtx({ application: app, job: jobs.find((j) => j.job_id === app.job_id) });
      return;
    }
    // "Entrevista" opens the dedicated scheduling form — never patches the
    // stage/notifies directly. Stage only changes once the employer actually
    // saves the interview (same /notify endpoint, wired in InterviewFormModal).
    if (stage === "interview") {
      setInterviewModalCtx({ application: app, initialInterview: app.interview_details || null });
      return;
    }
    setUpdatingStage(appId);
    setApplications((prev) => prev.map((a) => a.application_id === appId ? { ...a, stage } : a));
    if (stats && prevStage && prevStage !== stage) {
      setStats((s) => ({
        ...s,
        pipeline: { ...s.pipeline, [prevStage]: Math.max(0, (s.pipeline?.[prevStage] || 0) - 1), [stage]: (s.pipeline?.[stage] || 0) + 1 },
        recent_applications: s.recent_applications?.map((a) => a.application_id === appId ? { ...a, stage } : a),
      }));
    }
    try {
      await axios.patch(`${API}/applications/${appId}/stage`, { stage }, { headers: getAuthHeaders() });
      showToast(t("company.stageUpdatedOk"));
      // Stages that imply real candidate communication offer to send a real
      // notification (in_app + email) right after the stage change — never
      // fires automatically, the employer always reviews/edits/cancels first.
      if (NOTIFIABLE_STAGES.includes(stage) && prevStage !== stage) {
        setNotifyCtx({ application: { ...app, stage }, stage });
      }
    } catch (err) {
      setApplications(previous);
      showToast(err.response?.data?.detail || t("company.stageErrorGeneric"));
    } finally { setUpdatingStage(null); }
  };

  const withdrawOffer = async (app) => {
    if (!window.confirm(t("company.offerWithdrawConfirm"))) return;
    try {
      const res = await axios.patch(`${API}/offers/${app.offer.offer_id}/withdraw`, {}, { headers: getAuthHeaders() });
      setApplications((prev) => prev.map((a) => a.application_id === app.application_id ? { ...a, offer: res.data } : a));
      showToast(t("company.offerWithdrawnOk"));
    } catch (err) {
      showToast(err.response?.data?.detail || t("company.stageErrorGeneric"));
    }
  };

  const closeJob = async (jobId) => {
    if (!window.confirm(t("company.closeJob"))) return;
    const wasActive = jobs.find((j) => j.job_id === jobId)?.status === "active";
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: getAuthHeaders() });
      setJobs((prev) => prev.map((j) => j.job_id === jobId ? { ...j, status: "closed" } : j));
      if (wasActive && stats) setStats((s) => ({ ...s, total_jobs: Math.max(0, s.total_jobs - 1), active_jobs: s.active_jobs?.filter((j) => j.job_id !== jobId) }));
    } catch { showToast(t("company.editErrorGeneric")); }
  };

  const setJobStatus = async (jobId, status) => {
    const job = jobs.find((j) => j.job_id === jobId);
    try {
      await axios.put(`${API}/jobs/${jobId}`, { status }, { headers: getAuthHeaders() });
      setJobs((prev) => prev.map((j) => j.job_id === jobId ? { ...j, status } : j));
      if (stats && job) {
        if (status === "active" && job.status !== "active") {
          setStats((s) => ({ ...s, total_jobs: s.total_jobs + 1, active_jobs: [{ ...job, status }, ...(s.active_jobs || [])].slice(0, 5) }));
        } else if (job.status === "active" && status !== "active") {
          setStats((s) => ({ ...s, total_jobs: Math.max(0, s.total_jobs - 1), active_jobs: s.active_jobs?.filter((j) => j.job_id !== jobId) }));
        }
      }
    } catch (err) { if (!handleAccessRequired(err)) showToast(t("company.editErrorGeneric")); }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      const res = await axios.put(`${API}/my/company`, companyForm, { headers: getAuthHeaders() });
      setCompany(res.data); setEditingCompany(false);
      showToast(t("company.profileSavedOk"));
    } catch (err) { showToast(t("company.profileSaveError")); }
    finally { setSavingCompany(false); }
  };

  const MAX_LOGO_BYTES = 8 * 1024 * 1024;
  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLogoError("");
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setLogoError(t("company.logoInvalidType")); return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(t("company.logoTooLarge")); return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const uploadLogo = async () => {
    if (!logoFile) return;
    setLogoUploading(true);
    setLogoError("");
    try {
      const formData = new FormData();
      formData.append("file", logoFile);
      const res = await axios.post(`${API}/company/logo`, formData, { headers: getAuthHeaders() });
      setCompany((c) => (c ? { ...c, logo_url: res.data.logo_url } : c));
      setLogoFile(null); setLogoPreview(null);
      showToast(t("company.logoSavedOk"));
    } catch (err) {
      setLogoError(err.response?.data?.detail || t("company.logoUploadError"));
    } finally { setLogoUploading(false); }
  };

  const cancelLogoChange = () => { setLogoFile(null); setLogoPreview(null); setLogoError(""); };

  const removeLogo = async () => {
    if (!window.confirm(t("company.removeLogoConfirm"))) return;
    setLogoUploading(true);
    try {
      await axios.delete(`${API}/company/logo`, { headers: getAuthHeaders() });
      setCompany((c) => (c ? { ...c, logo_url: "" } : c));
      showToast(t("company.logoRemovedOk"));
    } catch { showToast(t("company.logoUploadError")); }
    finally { setLogoUploading(false); }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="company-dashboard" className="min-h-screen bg-slate-50">

      {toast && (
        <div style={{ position: "fixed", top: "80px", right: "24px", zIndex: 9999, background: "#028CF8", color: "white", padding: "12px 20px", borderRadius: "12px", fontSize: "13px", fontWeight: 600, fontFamily: "Poppins, sans-serif", boxShadow: "0 4px 14px rgba(15,23,42,0.25)", maxWidth: "320px" }} data-testid="company-toast">
          {toast}
        </div>
      )}

      {/* ── Dashboard Header — dark gradient, compact on mobile ── */}
      <section className="hero-infocity py-5 sm:py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <CompanyAvatar name={company?.name || user?.name} logo={company?.logo_url} size={44} rounded={16} />
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs font-semibold text-white uppercase tracking-widest">{t("company.dashTitle")}</p>
                <h1 className="font-poppins text-lg sm:text-2xl font-bold text-white truncate">{company?.name || user?.name}</h1>
              </div>
            </div>
            <button
              onClick={startNewJob}
              data-testid="post-job-header-button"
              className="hidden sm:flex items-center gap-2 h-10 px-5 bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold rounded-xl transition-all"
            >
              <Plus className="h-4 w-4" /> {t("tabs.postJob")}
            </button>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 pt-6 pb-6">
        <div className="flex gap-6 flex-col lg:flex-row">

          {/* ── Sidebar Navigation (desktop only — mobile uses bottom nav) ── */}
          <aside className="hidden lg:block lg:w-56 flex-shrink-0">
            <nav className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="company-tabs">
              {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  data-testid={`tab-${id}`}
                  onClick={() => (id === "post" ? startNewJob() : id === "messages" ? navigate("/messages") : setTab(id))}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm font-semibold transition-all border-l-2 ${
                    tab === id
                      ? "bg-[#028CF8]/5 text-[#028CF8] border-[#028CF8]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-transparent"
                  }`}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" /> {label}
                  {id === "post" && <span className="ml-auto h-5 w-5 rounded-full bg-[#028CF8] flex items-center justify-center"><Plus className="h-3 w-3 text-white" /></span>}
                  {id === "applicants" && jobUnreadCount > 0 && (
                    <span data-testid="sidebar-applicants-badge"
                      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#028CF8] text-white text-[11px] font-bold flex items-center justify-center leading-none">
                      {jobUnreadCount}
                    </span>
                  )}
                  {id === "messages" && unreadCount > 0 && (
                    <span data-testid="sidebar-messages-badge"
                      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#F55FA5] text-white text-[11px] font-bold flex items-center justify-center leading-none">
                      {unreadCount}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          {/* ── Main Content ── */}
          <main className="flex-1 min-w-0">

            {/* OVERVIEW */}
            {tab === "overview" && stats && (
              <div data-testid="overview-tab" className="space-y-4">

                {/* KPIs compactos — datos reales de /company/dashboard, sin hardcode */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="overview-kpi-row">
                  {[
                    { key: "active_jobs", label: t("company.kpiActiveJobs"), sublabel: null, icon: Briefcase, color: "#028CF8" },
                    { key: "new_candidates", label: t("company.kpiNewCandidates"), sublabel: t("company.kpiLast7Days"), icon: Users, color: "#f59e0b" },
                    { key: "upcoming_interviews", label: t("company.kpiUpcomingInterviews"), sublabel: null, icon: Calendar, color: "#8b5cf6" },
                    { key: "hires", label: t("company.kpiHires"), sublabel: t("company.kpiLast30Days"), icon: CheckCircle, color: "#10b981" },
                  ].map(({ key, label, sublabel, icon: Icon, color }) => (
                    <div key={key} data-testid={`kpi-card-${key}`} className="bg-white rounded-xl border border-[#E5E7EB] p-3.5 flex items-start gap-2.5">
                      <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}1A` }}>
                        <Icon className="h-4 w-4" style={{ color }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p data-testid={`kpi-value-${key}`} className="font-poppins text-xl font-bold text-slate-900 leading-none">{stats.kpis?.[key] ?? 0}</p>
                        <p className="text-[11px] font-semibold text-slate-500 leading-snug mt-1">{label}</p>
                        {/* Every card always renders this line (a non-breaking space when there's
                            no sublabel) so the number/title baselines line up identically across
                            all 4 KPI cards regardless of which ones have a sublabel. */}
                        <p className="text-[11px] font-normal text-slate-400 leading-snug">{sublabel || "\u00A0"}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Hiring Pipeline — barra de pasos compacta (stepper) con contador por
                    etapa. Mismas 6 etapas y misma fuente de datos (stats.pipeline).
                    3 columnas x 2 filas en mobile (para que las 6 etiquetas completas
                    entren sin truncarse), 6 columnas en una sola fila desde sm+. */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 sm:p-5 shadow-sm" data-testid="pipeline-overview">
                  <div className="mb-2.5 sm:mb-3">
                    <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-0.5 sm:mb-1">{t("company.funnel")}</p>
                    <h2 className="font-poppins text-lg font-bold text-slate-900">{t("company.hiringPipeline")}</h2>
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-white rounded-xl overflow-hidden border border-slate-100" data-testid="pipeline-stepper">
                    {PIPELINE_STAGES.map(({ key, color, bg, text }) => (
                      <div key={key} data-testid={`pipeline-stage-${key}`} className={`text-center py-3 px-1 ${bg}`}>
                        <p data-testid={`pipeline-count-${key}`} className="font-poppins text-lg sm:text-xl font-bold leading-none" style={{ color }}>
                          {stats.pipeline?.[key] || 0}
                        </p>
                        <p className={`text-[10px] sm:text-xs font-semibold ${text} leading-tight mt-1 whitespace-nowrap`}>{t(`pipeline.${key}`)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Próximas Entrevistas / Historial */}
                <UpcomingInterviewsWidget
                  interviews={stats.interviews}
                  t={t}
                  onViewCandidate={openCandidate}
                  onEditInterview={(app) => setInterviewModalCtx({ application: app, initialInterview: app.interview_details || null })}
                />

                {/* Active Jobs */}
                {stats.active_jobs?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("company.activeListings")}</p>
                        <h2 className="font-poppins text-xl font-bold text-slate-900">{t("company.activeJobs")}</h2>
                      </div>
                      <button onClick={() => setTab("jobs")} className="text-sm text-[#028CF8] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                        {t("company.viewAll")} <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-2.5">
                      {stats.active_jobs.map((job) => (
                        <div key={job.job_id} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm truncate">{job.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{formatJobLocation(job)} · {jobTypeLabel(job.job_type, t)}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-xs font-semibold text-[#028CF8]">{job.applications_count || 0} {t("company.applicants")}</span>
                            <Link to={`/jobs/${job.job_id}`} target="_blank" className="h-7 w-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#028CF8] transition-colors">
                              <Eye className="h-3.5 w-3.5" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mejores candidatos — solo si hay al menos una vacante activa */}
                {stats.active_jobs?.length > 0 && (
                  <TopCandidatesWidget API={API} t={t} onViewCv={setViewingCv} onViewCandidate={openCandidate} setTab={setTab} />
                )}

                {/* Recent Applications */}
                {stats.recent_applications?.length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("company.recentActivity")}</p>
                        <h2 className="font-poppins text-xl font-bold text-slate-900">{t("company.latestApps")}</h2>
                      </div>
                      <button onClick={() => setTab("applicants")} className="text-sm text-[#028CF8] font-semibold flex items-center gap-1 hover:gap-2 transition-all">
                        {t("company.viewAll")} <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {stats.recent_applications.map((app) => {
                        const stage = PIPELINE_STAGES.find((s) => s.key === app.stage);
                        return (
                          <CandidateSummaryCard
                            key={app.application_id}
                            cardTestId={`recent-application-${app.application_id}`}
                            avatarUrl={app.picture}
                            name={app.name}
                            line1={`${app.job_title} · ${timeAgo(app.applied_date, t)}`}
                            line2={stage && (
                              <span className={`px-2 py-0.5 rounded-full ${stage.bg} ${stage.text}`}>
                                {t(`pipeline.${stage.key}`)}
                              </span>
                            )}
                            profileAction={app.user_id ? { onClick: () => openCandidate(app), testId: `recent-application-view-${app.application_id}`, label: t("company.candidateProfileAction") } : null}
                            messageAction={app.user_id ? { onClick: () => openCandidate(app, true), testId: `recent-application-message-${app.application_id}`, label: t("company.candidateMessageAction") } : null}
                            thirdAction={
                              <CandidateStageAction
                                testId={`recent-application-stage-select-${app.application_id}`}
                                ariaLabel={t("company.changeStageAria")}
                                label={t("company.changeStageBtn")}
                                value={app.stage}
                                onChange={(e) => updateStage(app, e.target.value)}
                                disabled={updatingStage === app.application_id}
                              >
                                {PIPELINE_STAGES.map((s) => <option key={s.key} value={s.key}>{t(`pipeline.${s.key}`)}</option>)}
                              </CandidateStageAction>
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* POST A JOB */}
            {tab === "post" && (
              <div data-testid="post-job-tab">
                <div className="mb-5">
                  <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("postJobLabels.newListing")}</p>
                  <h2 className="font-poppins text-2xl font-bold text-slate-900">{editingJobId ? t("postJobLabels.editJobTitle") : t("postJobLabels.postJobTitle")}</h2>
                </div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm">
                  {postSuccess && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-100 rounded-xl px-4 py-3 mb-5 text-sm font-medium">
                      <CheckCircle className="h-4 w-4" /> {t("postJobLabels.successMsg")}
                    </div>
                  )}
                  {postStep === "preview" ? (
                    <JobPreviewCard
                      jobForm={jobForm}
                      company={company}
                      isEdit={!!editingJobId}
                      publishing={postingJob}
                      error={postError}
                      onBack={() => setPostStep("form")}
                      onPublish={handlePublishJob}
                    />
                  ) : (
                  <form onSubmit={handleShowPreview} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.jobTitleLabel")} *</label>
                        <input required data-testid="job-title-input" value={jobForm.title} onChange={(e) => setJobForm((f) => ({ ...f, title: e.target.value }))}
                          placeholder={t("company.titlePlaceholder")}
                          className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] transition-all" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.locationLabel")} *</label>
                        <LocationSelector
                          countryCode={jobForm.country_code}
                          admin1Code={jobForm.admin1_code}
                          admin1Name={jobForm.admin1_name}
                          admin2Code={jobForm.admin2_code}
                          admin2Name={jobForm.admin2_name}
                          city={jobForm.city}
                          locationVerified={jobForm.location_verified}
                          onChange={handleLocationChange}
                          requireFields={false}
                        />
                        {editingJobId && !jobForm.country_code && jobForm.location && (
                          <p className="text-xs text-slate-400 mt-1.5" data-testid="legacy-location-hint">
                            {t("postJobLabels.currentLocationLabel")}: <span className="font-medium text-slate-600">{jobForm.location}</span>
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.categoryLabel")} *</label>
                        <select required data-testid="job-category-input"
                          value={JOB_CATEGORIES.includes(jobForm.category) ? jobForm.category : OTHER_CATEGORY}
                          onChange={(e) => setJobForm((f) => ({ ...f, category: e.target.value === OTHER_CATEGORY ? "" : e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700 transition-all">
                          {JOB_CATEGORIES.map((c) => <option key={c} value={c}>{t(`categories.${c}`)}</option>)}
                          <option value={OTHER_CATEGORY}>{t("categories.Other")}</option>
                        </select>
                        {!JOB_CATEGORIES.includes(jobForm.category) && (
                          <input required data-testid="job-category-other-input" value={jobForm.category || ""}
                            onChange={(e) => setJobForm((f) => ({ ...f, category: e.target.value }))}
                            placeholder={t("postJobLabels.categoryOtherPlaceholder")}
                            className="mt-2 w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.jobTypeLabel")}</label>
                        <select data-testid="job-type-input"
                          value={JOB_TYPES.includes(jobForm.job_type) ? jobForm.job_type : OTHER_JOB_TYPE}
                          onChange={(e) => setJobForm((f) => ({ ...f, job_type: e.target.value === OTHER_JOB_TYPE ? "" : e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700">
                          {JOB_TYPES.map((jt) => <option key={jt} value={jt}>{t(`jobCard.${JOB_TYPE_KEY_MAP[jt]}`)}</option>)}
                          <option value={OTHER_JOB_TYPE}>{t("jobCard.typeOther")}</option>
                        </select>
                        {!JOB_TYPES.includes(jobForm.job_type) && (
                          <input data-testid="job-type-other-input" value={jobForm.job_type || ""}
                            onChange={(e) => setJobForm((f) => ({ ...f, job_type: e.target.value }))}
                            placeholder={t("postJobLabels.jobTypeOtherPlaceholder")}
                            className="mt-2 w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.experienceLabel")}</label>
                        <select data-testid="job-experience-input"
                          value={EXPERIENCE_LEVELS.includes(jobForm.experience_level) ? jobForm.experience_level : OTHER_EXPERIENCE}
                          onChange={(e) => setJobForm((f) => ({ ...f, experience_level: e.target.value === OTHER_EXPERIENCE ? "" : e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700">
                          {EXPERIENCE_LEVELS.map((l) => <option key={l} value={l}>{t(`jobCard.${EXPERIENCE_KEY_MAP[l]}`)}</option>)}
                          <option value={OTHER_EXPERIENCE}>{t("jobCard.expOther")}</option>
                        </select>
                        {!EXPERIENCE_LEVELS.includes(jobForm.experience_level) && (
                          <input data-testid="job-experience-other-input" value={jobForm.experience_level || ""}
                            onChange={(e) => setJobForm((f) => ({ ...f, experience_level: e.target.value }))}
                            placeholder={t("postJobLabels.experienceOtherPlaceholder")}
                            className="mt-2 w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                        )}
                      </div>
                      <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.salaryMinLabel")}</label>
                          <input type="number" data-testid="salary-min-input" value={jobForm.salary_min} onChange={(e) => setJobForm((f) => ({ ...f, salary_min: e.target.value }))} placeholder="80000"
                            disabled={!jobForm.salary_visible}
                            className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] disabled:bg-slate-50 disabled:text-slate-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.salaryMaxLabel")}</label>
                          <input type="number" data-testid="salary-max-input" value={jobForm.salary_max} onChange={(e) => setJobForm((f) => ({ ...f, salary_max: e.target.value }))} placeholder="120000"
                            disabled={!jobForm.salary_visible}
                            className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] disabled:bg-slate-50 disabled:text-slate-400" />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("geo.currencyLabel")}</label>
                          <select data-testid="salary-currency-select" value={jobForm.salary_currency}
                            disabled={!jobForm.salary_visible}
                            onChange={(e) => { setCurrencyManual(true); setJobForm((f) => ({ ...f, salary_currency: e.target.value })); }}
                            className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700 disabled:bg-slate-50 disabled:text-slate-400">
                            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} — {t(`geo.currencyNames.${c.code}`)}</option>)}
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.salaryPeriodLabel")}</label>
                          <select data-testid="salary-period-select" value={jobForm.salary_period}
                            disabled={!jobForm.salary_visible}
                            onChange={(e) => setJobForm((f) => ({ ...f, salary_period: e.target.value }))}
                            className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700 disabled:bg-slate-50 disabled:text-slate-400">
                            <option value="hour">{t("postJobLabels.periodHour")}</option>
                            <option value="month">{t("postJobLabels.periodMonth")}</option>
                            <option value="year">{t("postJobLabels.periodYear")}</option>
                          </select>
                        </div>
                        <div className="flex items-center gap-2.5 sm:col-span-1">
                          <input type="checkbox" id="hide-salary-cb" data-testid="hide-salary-checkbox"
                            checked={!jobForm.salary_visible}
                            onChange={(e) => setJobForm((f) => ({ ...f, salary_visible: !e.target.checked }))}
                            className="h-4 w-4 rounded border-slate-300 text-[#028CF8] focus:ring-[#028CF8]" />
                          <label htmlFor="hide-salary-cb" className="text-sm font-medium text-slate-700 cursor-pointer">
                            {t("postJobLabels.hideSalaryLabel")}
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Modalidad de trabajo — Presencial / Híbrido / Remoto (reemplaza el checkbox "es remoto") */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.workModeLabel")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="work-mode-selector">
                        {[
                          { value: "onsite", label: t("postJobLabels.workModeOnsite") },
                          { value: "hybrid", label: t("postJobLabels.workModeHybrid") },
                          { value: "remote", label: t("postJobLabels.workModeRemote") },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`work-mode-option-${opt.value}`}
                            onClick={() => setJobForm((f) => ({ ...f, work_mode: opt.value }))}
                            style={{
                              padding: "10px 12px", borderRadius: "12px", fontSize: "13px", fontWeight: 600,
                              fontFamily: "Poppins, sans-serif", textAlign: "left", cursor: "pointer",
                              transition: "all 0.15s",
                              border: `1.5px solid ${jobForm.work_mode === opt.value ? "#028CF8" : "#E2E8F0"}`,
                              backgroundColor: jobForm.work_mode === opt.value ? "#028CF814" : "white",
                              color: jobForm.work_mode === opt.value ? "#028CF8" : "#64748B",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {jobForm.work_mode === "onsite" && (
                        <div className="mt-3">
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.addressLabel")}</label>
                          <AddressAutocomplete
                            testId="job-address-input"
                            value={jobForm.address}
                            countryCode={jobForm.country_code}
                            admin1Name={jobForm.admin1_name}
                            city={jobForm.city}
                            placeholder={t("postJobLabels.addressPlaceholder")}
                            onChange={(patch) => setJobForm((f) => ({ ...f, ...patch }))}
                          />
                          <p className="text-xs text-slate-400 mt-1.5">{t("postJobLabels.addressHint")}</p>
                        </div>
                      )}
                      {jobForm.work_mode === "remote" && (
                        <div className="mt-3 p-3.5 bg-slate-50 rounded-xl border border-slate-200">
                          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.remoteCountriesLabel")}</p>
                          <div className="flex flex-wrap gap-2" data-testid="remote-countries-selector">
                            {["ES", "UY", "US", "AR", "BR"].map((code) => {
                              const active = jobForm.remote_countries.includes(code);
                              return (
                                <button
                                  key={code}
                                  type="button"
                                  data-testid={`remote-country-${code}`}
                                  onClick={() => setJobForm((f) => ({
                                    ...f,
                                    remote_countries: active ? f.remote_countries.filter((c) => c !== code) : [...f.remote_countries, code],
                                  }))}
                                  style={{
                                    padding: "6px 12px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 600,
                                    fontFamily: "Poppins, sans-serif", cursor: "pointer", transition: "all 0.15s",
                                    border: `1.5px solid ${active ? "#028CF8" : "#E2E8F0"}`,
                                    backgroundColor: active ? "#028CF814" : "white",
                                    color: active ? "#028CF8" : "#64748B",
                                  }}
                                >
                                  {t(`geo.countryNames.${code}`)}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Per-listing company identity — public vs hidden, independent from company ownership */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("companyVisibility.question")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="company-visibility-selector">
                        {[
                          { value: "public", label: t("companyVisibility.showCompany") },
                          { value: "hidden", label: t("companyVisibility.hideCompany") },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`company-visibility-${opt.value}`}
                            onClick={() => setJobForm((f) => ({ ...f, company_visibility: opt.value }))}
                            style={{
                              padding: "10px 12px", borderRadius: "12px", fontSize: "13px", fontWeight: 600,
                              fontFamily: "Poppins, sans-serif", textAlign: "left", cursor: "pointer",
                              transition: "all 0.15s",
                              border: `1.5px solid ${jobForm.company_visibility === opt.value ? "#028CF8" : "#E2E8F0"}`,
                              backgroundColor: jobForm.company_visibility === opt.value ? "#028CF814" : "white",
                              color: jobForm.company_visibility === opt.value ? "#028CF8" : "#64748B",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>

                      {jobForm.company_visibility === "hidden" && (
                        <div className="mt-3 p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{t("companyVisibility.hiddenNotice")}</p>
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("companyVisibility.chooseDisplayLabel")}</label>
                            <select
                              data-testid="company-display-preset-select"
                              value={jobForm.company_display_preset}
                              onChange={(e) => setJobForm((f) => ({ ...f, company_display_preset: e.target.value }))}
                              className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:border-[#028CF8] bg-white text-slate-700"
                            >
                              {getAllPresetOptions(jobForm.category, t).map((opt) => (
                                <option key={opt.key} value={opt.key}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          {jobForm.company_display_preset === "other" && (
                            <input
                              data-testid="company-display-custom-input"
                              value={jobForm.company_display_custom}
                              onChange={(e) => setJobForm((f) => ({ ...f, company_display_custom: e.target.value }))}
                              placeholder={t("companyVisibility.presetOtherExample")}
                              maxLength={80}
                              className="w-full h-11 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]"
                            />
                          )}
                          <div className="border-t border-slate-200 pt-3">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1.5">{t("companyVisibility.previewTitle")}</p>
                            <p className="text-sm font-bold text-slate-900" data-testid="company-visibility-preview-title">{jobForm.title || t("postJobLabels.jobTitleLabel")}</p>
                            <p className="text-sm text-[#028CF8] font-semibold" data-testid="company-visibility-preview-label">
                              {jobForm.company_display_preset === "other"
                                ? (sanitizeCustomLabel(jobForm.company_display_custom) || t("companyVisibility.presetOtherPlaceholder"))
                                : getPresetLabel(jobForm.company_display_preset, jobForm.category, t)}
                            </p>
                            {jobForm.location && <p className="text-xs text-slate-500 mt-0.5">{jobForm.location}</p>}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Urgency / immediacy — real fields (is_urgent, start_date), not cosmetic */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.urgencyLabel")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="urgency-selector">
                        {[
                          { value: "normal",    label: t("postJobLabels.urgencyNormal"),    color: "#64748B" },
                          { value: "urgent",    label: t("postJobLabels.urgencyUrgent"),    color: "#F55FA5" },
                          { value: "immediate", label: t("postJobLabels.urgencyImmediate"), color: "#028CF8" },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`urgency-option-${opt.value}`}
                            onClick={() => setJobForm((f) => ({ ...f, urgency: opt.value }))}
                            style={{
                              padding: "10px 12px", borderRadius: "12px", fontSize: "13px", fontWeight: 600,
                              fontFamily: "Poppins, sans-serif", textAlign: "left", cursor: "pointer",
                              transition: "all 0.15s",
                              border: `1.5px solid ${jobForm.urgency === opt.value ? opt.color : "#E2E8F0"}`,
                              backgroundColor: jobForm.urgency === opt.value ? `${opt.color}14` : "white",
                              color: jobForm.urgency === opt.value ? opt.color : "#64748B",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {jobForm.urgency !== "normal" && (
                        <input
                          data-testid="shift-label-input"
                          value={jobForm.shift_label}
                          onChange={(e) => setJobForm((f) => ({ ...f, shift_label: e.target.value }))}
                          placeholder={t("postJobLabels.shiftLabelPlaceholder")}
                          className="w-full h-10 mt-2 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("postJobLabels.descriptionLabel")} *</label>
                      <textarea required data-testid="job-description-input" rows={6} value={jobForm.description} onChange={(e) => setJobForm((f) => ({ ...f, description: e.target.value }))}
                        placeholder={t("company.descriptionPlaceholder")}
                        className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none transition-all" />
                      <button type="button" data-testid="improve-with-ai-button" disabled={improvingDescription}
                        onClick={handleImproveDescriptionWithAI}
                        className="mt-2 text-sm text-[#F55FA5] font-semibold hover:underline disabled:opacity-50 flex items-center gap-1.5">
                        {improvingDescription ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("postJobLabels.improvingWithAI")}</> : t("postJobLabels.improveWithAI")}
                      </button>
                      {aiDescriptionProposal && (
                        <div className="mt-3 p-3.5 bg-pink-50 border border-pink-200 rounded-xl" data-testid="ai-improve-proposal-panel">
                          <p className="text-xs font-semibold text-[#F55FA5] uppercase tracking-wide mb-1.5">{t("postJobLabels.aiImproveModalTitle")}</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3" data-testid="ai-improve-proposal-text">{aiDescriptionProposal}</p>
                          <div className="flex gap-2">
                            <button type="button" data-testid="ai-improve-accept-button"
                              onClick={() => { setJobForm((f) => ({ ...f, description: aiDescriptionProposal })); setAiDescriptionProposal(null); }}
                              className="flex-1 h-10 bg-[#F55FA5] hover:bg-[#e04f95] text-white font-semibold rounded-lg text-sm transition-all">
                              {t("postJobLabels.aiImproveAccept")}
                            </button>
                            <button type="button" data-testid="ai-improve-cancel-button" onClick={() => setAiDescriptionProposal(null)}
                              className="flex-1 h-10 border border-slate-200 bg-white text-slate-600 font-semibold rounded-lg text-sm hover:bg-slate-50 transition-all">
                              {t("postJobLabels.aiImproveCancel")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Requirements */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.requirementsLabel")}</label>
                      <div className="space-y-2">
                        {jobForm.requirements.map((r, i) => (
                          <div key={i} className="flex gap-2">
                            <input value={r} onChange={(e) => { const arr = [...jobForm.requirements]; arr[i] = e.target.value; setJobForm((f) => ({ ...f, requirements: arr })); }}
                              placeholder={`${t("company.requirementPlaceholder")} ${i + 1}`}
                              className="flex-1 h-10 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:border-[#028CF8]" />
                            {jobForm.requirements.length > 1 && (
                              <button type="button" onClick={() => setJobForm((f) => ({ ...f, requirements: f.requirements.filter((_, j) => j !== i) }))}
                                className="h-10 w-10 rounded-xl border border-slate-200 text-red-400 hover:bg-red-50 flex items-center justify-center transition-all">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => setJobForm((f) => ({ ...f, requirements: [...f.requirements, ""] }))}
                        className="mt-2 text-sm text-[#028CF8] font-semibold hover:underline">
                        {t("postJobLabels.addRequirement")}
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.benefitsLabel")}</label>
                      <div className="space-y-2">
                        {jobForm.benefits.map((b, i) => (
                          <div key={i} className="flex gap-2">
                            <input value={b} onChange={(e) => { const arr = [...jobForm.benefits]; arr[i] = e.target.value; setJobForm((f) => ({ ...f, benefits: arr })); }}
                              placeholder={`${t("postJobLabels.benefitsLabel")} ${i + 1}`}
                              className="flex-1 h-10 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:border-[#028CF8]" />
                            {jobForm.benefits.length > 1 && (
                              <button type="button" onClick={() => setJobForm((f) => ({ ...f, benefits: f.benefits.filter((_, j) => j !== i) }))}
                                className="h-10 w-10 rounded-xl border border-slate-200 text-red-400 hover:bg-red-50 flex items-center justify-center transition-all">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => setJobForm((f) => ({ ...f, benefits: [...f.benefits, ""] }))}
                        className="mt-2 text-sm text-[#028CF8] font-semibold hover:underline">
                        {t("postJobLabels.addBenefit")}
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">{t("postJobLabels.applicationMethodLabel")}</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="application-method-selector">
                        {[
                          { value: "internal", label: t("postJobLabels.applyMethodInternal") },
                          { value: "external", label: t("postJobLabels.applyMethodExternal") },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            data-testid={`application-method-option-${opt.value}`}
                            onClick={() => setJobForm((f) => ({ ...f, application_method: opt.value }))}
                            style={{
                              padding: "10px 12px", borderRadius: "12px", fontSize: "13px", fontWeight: 600,
                              fontFamily: "Poppins, sans-serif", textAlign: "left", cursor: "pointer",
                              transition: "all 0.15s",
                              border: `1.5px solid ${jobForm.application_method === opt.value ? "#028CF8" : "#E2E8F0"}`,
                              backgroundColor: jobForm.application_method === opt.value ? "#028CF814" : "white",
                              color: jobForm.application_method === opt.value ? "#028CF8" : "#64748B",
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {jobForm.application_method === "external" && (
                        <input data-testid="external-link-input" value={jobForm.external_apply_link} onChange={(e) => setJobForm((f) => ({ ...f, external_apply_link: e.target.value }))}
                          placeholder="https://company.com/careers/apply"
                          className="w-full h-11 mt-2 border border-slate-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                      )}
                    </div>
                    {postError && (
                      <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3" data-testid="post-job-error">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" /> {postError}
                      </div>
                    )}
                    <div className={editingJobId ? "relative z-[51] flex flex-col sm:flex-row gap-2.5 sm:gap-3" : "flex gap-3"}>
                      <button type="submit" data-testid="submit-job-button"
                        className={`${editingJobId ? "w-full sm:flex-1 h-[52px] sm:h-12" : "flex-1 h-12"} bg-[#028CF8] hover:bg-[#0270C5] text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm whitespace-nowrap`}>
                        {t("postJobLabels.previewBtn")}
                      </button>
                      {editingJobId && (
                        <button type="button" onClick={cancelEditJob} data-testid="cancel-edit-job-button"
                          className="w-full sm:flex-1 h-[52px] sm:h-12 border border-slate-200 bg-white text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition-all text-sm whitespace-nowrap">
                          {t("postJobLabels.cancelEditBtn")}
                        </button>
                      )}
                    </div>
                  </form>
                  )}
                </div>
              </div>
            )}

            {/* MY LISTINGS */}
            {tab === "jobs" && (
              <div data-testid="jobs-tab">
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <div>
                    <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("postJobLabels.management")}</p>
                    <h2 className="font-poppins text-2xl font-bold text-slate-900">{t("tabs.myListings")}</h2>
                  </div>
                  <button onClick={startNewJob} className="flex items-center gap-2 h-10 px-5 bg-[#028CF8] text-white text-sm font-semibold rounded-xl hover:bg-[#0270C5] transition-all">
                    <Plus className="h-4 w-4" /> {t("postJobLabels.newJob")}
                  </button>
                </div>

                {jobs.length > 0 && (
                  <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <input
                        type="text" data-testid="jobs-search-input" value={jobsSearch}
                        onChange={(e) => setJobsSearch(e.target.value)}
                        placeholder={t("company.searchListingsPlaceholder")}
                        className="w-full h-10 pl-9 pr-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]"
                      />
                    </div>
                    <select data-testid="jobs-status-filter" value={jobsStatusFilter} onChange={(e) => setJobsStatusFilter(e.target.value)}
                      className="h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]">
                      <option value="all">{t("company.filterAll")}</option>
                      <option value="active">{t("postJobLabels.active")}</option>
                      <option value="paused">{t("postJobLabels.paused")}</option>
                      <option value="closed">{t("postJobLabels.closed")}</option>
                    </select>
                    <select data-testid="jobs-sort-select" value={jobsSort} onChange={(e) => setJobsSort(e.target.value)}
                      className="h-10 px-3 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]">
                      <option value="newest">{t("company.sortNewest")}</option>
                      <option value="oldest">{t("company.sortOldest")}</option>
                      <option value="applicants">{t("company.sortApplicants")}</option>
                    </select>
                  </div>
                )}

                {(() => {
                  const visibleJobs = jobs
                    .filter((j) => jobsStatusFilter === "all" || j.status === jobsStatusFilter)
                    .filter((j) => !jobsSearch.trim() || j.title.toLowerCase().includes(jobsSearch.trim().toLowerCase()))
                    .sort((a, b) => {
                      if (jobsSort === "oldest") return new Date(a.posted_date) - new Date(b.posted_date);
                      if (jobsSort === "applicants") return (b.applications_count || 0) - (a.applications_count || 0);
                      return new Date(b.posted_date) - new Date(a.posted_date);
                    });

                  if (jobs.length === 0) {
                    return (
                      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-12 text-center shadow-sm">
                        <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="font-poppins text-lg font-semibold text-slate-700 mb-2">{t("company.noJobsYet")}</h3>
                        <button onClick={startNewJob} className="h-10 px-6 bg-[#028CF8] text-white text-sm font-semibold rounded-xl mt-2 hover:bg-[#0270C5] transition-all">
                          {t("company.postFirstJobBtn")}
                        </button>
                      </div>
                    );
                  }
                  if (visibleJobs.length === 0) {
                    return (
                      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-10 text-center shadow-sm" data-testid="jobs-no-results">
                        <Search className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-sm text-slate-500">{t("company.noListingsMatch")}</p>
                      </div>
                    );
                  }
                  return (
                  <div className="space-y-2.5">
                    {visibleJobs.map((job) => (
                      <div key={job.job_id} data-testid={`job-listing-${job.job_id}`}
                        className={`bg-white rounded-2xl border border-[#E5E7EB] p-4 shadow-sm transition-all duration-200 ${job.status === "active" ? "hover:shadow-lg hover:-translate-y-0.5 hover:border-[#028CF8]/30" : job.status === "paused" ? "border-amber-200" : "bg-slate-50 border-slate-200"}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className={`font-semibold truncate ${job.status === "active" ? "text-slate-900" : job.status === "paused" ? "text-slate-700" : "text-slate-400"}`}>{job.title}</h3>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                                job.status === "active" ? "bg-green-50 text-green-700" :
                                job.status === "paused" ? "bg-amber-50 text-amber-700" :
                                "bg-slate-100 text-slate-500"
                              }`}>
                                {t(`postJobLabels.${job.status}`) || job.status}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                              <span>{formatJobLocation(job)}</span>
                              <span>{jobTypeLabel(job.job_type, t)}</span>
                              <span>{experienceLabel(job.experience_level, t)}</span>
                              {job.remote && <span className="text-teal-600">{t("jobCard.remote")}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Link to={`/jobs/${job.job_id}`} target="_blank"
                              className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#028CF8] hover:border-[#028CF8]/30 transition-all"
                              title={t("company.viewJob")}>
                              <Eye className="h-3.5 w-3.5" />
                            </Link>
                            {/* Edit */}
                            <button onClick={() => startEditJob(job)} data-testid={`edit-job-${job.job_id}`}
                              className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#028CF8] hover:border-[#028CF8]/30 transition-all"
                              title={t("company.editTooltip")}>
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            {/* Pause / Resume */}
                            {job.status === "active" && (
                              <button
                                onClick={() => setJobStatus(job.job_id, "paused")}
                                data-testid={`pause-job-${job.job_id}`}
                                className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-amber-500 hover:border-amber-200 transition-all"
                                title={t("company.pauseTooltip")}
                              >
                                <span style={{ fontSize: "14px", lineHeight: 1 }}>⏸</span>
                              </button>
                            )}
                            {job.status === "paused" && (
                              <button
                                onClick={() => setJobStatus(job.job_id, "active")}
                                data-testid={`resume-job-${job.job_id}`}
                                className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-green-600 hover:border-green-200 transition-all"
                                title={t("company.resumeTooltip")}
                              >
                                <span style={{ fontSize: "14px", lineHeight: 1 }}>▶</span>
                              </button>
                            )}
                            {/* Close job */}
                            {(job.status === "active" || job.status === "paused") && (
                              <button onClick={() => closeJob(job.job_id)} data-testid={`close-job-${job.job_id}`}
                                className="h-8 w-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-red-500 hover:border-red-200 transition-all"
                                title={t("company.closeTooltip")}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-5 mt-2.5 pt-2.5 border-t border-slate-100 text-xs text-slate-500">
                          <span><strong className="text-slate-900">{job.applications_count || 0}</strong> {t("company.applicants")}</span>
                          <span><strong className="text-slate-900">{job.views_count || 0}</strong> {t("company.views")}</span>
                          <span>{t("company.postedOn")} {timeAgo(job.posted_date, t)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  );
                })()}
              </div>
            )}

            {/* APPLICANTS */}
            {tab === "applicants" && (
              <div data-testid="applicants-tab">
                <TopCandidatesSection
                  jobs={jobs}
                  applications={applications}
                  updatingStage={updatingStage}
                  updateStage={updateStage}
                  API={API}
                  t={t}
                  onViewCv={setViewingCv}
                  onViewCandidate={openCandidate}
                  onEditInterview={(app) => setInterviewModalCtx({ application: app, initialInterview: app.interview_details || null })}
                  onWithdrawOffer={withdrawOffer}
                />
              </div>
            )}

            {/* COMPANY PROFILE */}
            {tab === "company" && (
              <div data-testid="company-profile-tab">
                <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-xs font-semibold text-[#028CF8] uppercase tracking-widest mb-1">{t("company.settings")}</p>
                    <h2 className="font-poppins text-2xl font-bold text-slate-900">{t("tabs.companyProfile")}</h2>
                  </div>
                  {company && typeof company.profile_completeness === "number" && (
                    <div className="flex items-center gap-2.5" data-testid="company-profile-completeness">
                      <div className="w-28 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full bg-[#028CF8] rounded-full transition-all" style={{ width: `${company.profile_completeness}%` }} />
                      </div>
                      <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">{t("company.profileCompleteness").replace("{pct}", company.profile_completeness)}</span>
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm">
                  {/* ── Logo + company name — always visible, "Cambiar/Eliminar logo"
                      only while editing. Preview shows immediately on file pick;
                      nothing is persisted until "Guardar logo" is pressed. ── */}
                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-100">
                    <CompanyAvatar name={company?.name} logo={logoPreview || company?.logo_url} size={72} rounded={18} />
                    <div className="flex-1 min-w-0">
                      <p className="font-poppins font-bold text-slate-900 text-base truncate">{company?.name || t("company.companyName")}</p>
                      {editingCompany && (
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <label data-testid="company-logo-file-label"
                            className="cursor-pointer inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#028CF8]/10 text-[#028CF8] text-xs font-semibold hover:bg-[#028CF8]/15 transition-colors">
                            <Camera className="h-3.5 w-3.5" /> {t("company.changeLogo")}
                            <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="company-logo-file-input"
                              onChange={handleLogoFileChange} className="hidden" />
                          </label>
                          {logoFile ? (
                            <>
                              <button onClick={uploadLogo} disabled={logoUploading} data-testid="company-save-logo-button"
                                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#028CF8] text-white text-xs font-semibold disabled:opacity-50 transition-colors">
                                {logoUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />} {t("company.saveLogo")}
                              </button>
                              <button onClick={cancelLogoChange} disabled={logoUploading} data-testid="company-cancel-logo-button"
                                className="h-9 px-3 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50 transition-colors">
                                {t("company.cancelBtn")}
                              </button>
                            </>
                          ) : company?.logo_url ? (
                            <button onClick={removeLogo} disabled={logoUploading} data-testid="company-remove-logo-button"
                              className="h-9 px-3 rounded-lg text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors">
                              {t("company.removeLogo")}
                            </button>
                          ) : null}
                        </div>
                      )}
                      {logoError && <p className="text-xs text-red-500 mt-1.5" data-testid="company-logo-error">{logoError}</p>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-6">
                    <p className="text-sm text-slate-500">{t("company.keepUpdated")}</p>
                    <button onClick={() => setEditingCompany(!editingCompany)}
                      className={`flex items-center gap-1.5 h-8 px-4 rounded-xl text-xs font-semibold transition-all ${editingCompany ? "bg-slate-100 text-slate-700" : "bg-[#028CF8]/10 text-[#028CF8]"}`}
                      data-testid="edit-company-button">
                      {editingCompany ? <X className="h-3.5 w-3.5" /> : <Edit2 className="h-3.5 w-3.5" />} {editingCompany ? t("company.cancelBtn") : t("company.editCompany")}
                    </button>
                  </div>

                  {company && company.verification_status !== "verified" && (
                    <div
                      data-testid="verification-banner"
                      className={`mb-6 rounded-xl border p-4 text-sm ${company.verification_status === "needs_review" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}
                    >
                      <p className="font-semibold mb-1">
                        {company.verification_status === "needs_review" ? t("company.verificationNeedsReviewTitle") : t("company.verificationPendingTitle")}
                      </p>
                      <p className="text-xs opacity-90">
                        {company.verification_status === "needs_review" ? t("company.verificationNeedsReviewDesc") : t("company.verificationPendingDesc")}
                      </p>
                    </div>
                  )}
                  {company && company.verification_status === "verified" && (
                    <div className="mb-6 flex items-center gap-2" data-testid="verification-verified-row">
                      <VerificationBadge />
                      <span className="text-xs text-slate-400">{t("company.verificationVerifiedDesc")}</span>
                    </div>
                  )}

                  {editingCompany ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { name: "name", label: t("company.companyName"), type: "text" },
                        { name: "website", label: t("company.website"), type: "url" },
                        { name: "industry", label: t("company.industry"), type: "text" },
                        { name: "location", label: t("company.hq"), type: "text" },
                        { name: "legal_name", label: t("company.legalName"), type: "text" },
                        { name: "tax_id", label: t("company.taxId"), type: "text" },
                        { name: "phone", label: t("company.phone"), type: "tel" },
                      ].map(({ name, label, type }) => (
                        <div key={name}>
                          <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{label}</label>
                          <input type={type} data-testid={`company-${name}`} value={companyForm[name] || ""} onChange={(e) => setCompanyForm((f) => ({ ...f, [name]: e.target.value }))}
                            className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                        </div>
                      ))}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.size")}</label>
                        <select data-testid="company-size" value={companyForm.size || ""} onChange={(e) => setCompanyForm((f) => ({ ...f, size: e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]">
                          <option value="">{t("company.sizePlaceholder")}</option>
                          {companyForm.size && !COMPANY_SIZE_OPTIONS.includes(companyForm.size) && (
                            <option value={companyForm.size}>{companyForm.size}</option>
                          )}
                          {COMPANY_SIZE_OPTIONS.map((code) => (
                            <option key={code} value={code}>{t(`company.${COMPANY_SIZE_LABEL_KEYS[code]}`)}</option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-400 mt-1.5">{t("company.sizeHelp")}</p>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.address")}</label>
                        <AddressAutocomplete
                          testId="company-address"
                          value={companyForm.address}
                          countryCode={companyForm.country_code}
                          admin1Name={companyForm.admin1_name}
                          city={companyForm.city}
                          placeholder={t("company.addressPlaceholder")}
                          onChange={(patch) => setCompanyForm((f) => ({ ...f, ...patch }))}
                        />
                        <p className="text-xs text-slate-400 mt-1.5">{t("company.addressHint")}</p>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.description")}</label>
                        <textarea data-testid="company-description" rows={4} value={companyForm.description || ""} onChange={(e) => setCompanyForm((f) => ({ ...f, description: e.target.value }))}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
                      </div>

                      {/* ── Perfil de Empresa expandido (Fase 3) — todos opcionales, nunca bloquean publicar empleos ── */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.foundedYear")}</label>
                        <input type="number" data-testid="company-founded-year" value={companyForm.founded_year || ""} min="1800" max={new Date().getFullYear()}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, founded_year: e.target.value ? Number(e.target.value) : null }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.linkedinUrl")}</label>
                        <input type="url" data-testid="company-linkedin-url" value={companyForm.linkedin_url || ""} placeholder="https://linkedin.com/company/..."
                          onChange={(e) => setCompanyForm((f) => ({ ...f, linkedin_url: e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.instagramUrl")}</label>
                        <input type="url" data-testid="company-instagram-url" value={companyForm.instagram_url || ""} placeholder="https://instagram.com/..."
                          onChange={(e) => setCompanyForm((f) => ({ ...f, instagram_url: e.target.value }))}
                          className="w-full h-11 border border-slate-200 rounded-xl px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8]" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.culture")}</label>
                        <textarea data-testid="company-culture" rows={3} value={companyForm.culture || ""} placeholder={t("company.culturePlaceholder")}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, culture: e.target.value }))}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1.5">{t("company.benefits")}</label>
                        <textarea data-testid="company-benefits" rows={3} placeholder={t("company.benefitsPlaceholder")}
                          value={(companyForm.benefits || []).join("\n")}
                          onChange={(e) => setCompanyForm((f) => ({ ...f, benefits: e.target.value.split("\n") }))}
                          className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#028CF8]/20 focus:border-[#028CF8] resize-none" />
                        <p className="text-xs text-slate-400 mt-1.5">{t("company.benefitsHint")}</p>
                      </div>

                      <div className="sm:col-span-2">
                        <button onClick={saveCompany} disabled={savingCompany} data-testid="save-company-button"
                          className="h-11 px-8 bg-[#028CF8] hover:bg-[#0270C5] text-white font-semibold rounded-xl text-sm disabled:opacity-50 flex items-center gap-2 transition-all">
                          {savingCompany ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4" /> {t("company.saveCompany")}</>}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {company ? (
                        [
                          { label: t("company.companyName"), value: company.name },
                          { label: t("company.industry"), value: company.industry },
                          { label: t("company.size"), value: COMPANY_SIZE_LABEL_KEYS[company.size] ? t(`company.${COMPANY_SIZE_LABEL_KEYS[company.size]}`) : company.size },
                          { label: t("company.hq"), value: company.location },
                          { label: t("company.address"), value: company.address },
                          { label: t("company.website"), value: company.website },
                          { label: t("company.foundedYear"), value: company.founded_year },
                          { label: t("company.linkedinUrl"), value: company.linkedin_url },
                          { label: t("company.instagramUrl"), value: company.instagram_url },
                        ].filter((x) => x.value).map(({ label, value }, i) => (
                          <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">{label}</p>
                            <p className="font-semibold text-slate-900 text-sm break-all">{value}</p>
                          </div>
                        ))
                      ) : (
                        <div className="sm:col-span-2 text-sm text-slate-500" data-testid="no-company-profile">
                          {t("company.noCompanyProfileYet")}
                        </div>
                      )}
                      {company?.description && (
                        <div className="sm:col-span-2 p-4 bg-slate-50 rounded-xl border border-slate-100">
                          <p className="text-xs text-slate-400 mb-1">{t("company.description")}</p>
                          <p className="text-sm text-slate-600 leading-relaxed">{company.description}</p>
                        </div>
                      )}
                      {company?.culture && (
                        <div className="sm:col-span-2 p-4 bg-slate-50 rounded-xl border border-slate-100">
                          <p className="text-xs text-slate-400 mb-1">{t("company.culture")}</p>
                          <p className="text-sm text-slate-600 leading-relaxed">{company.culture}</p>
                        </div>
                      )}
                      {company?.benefits?.length > 0 && (
                        <div className="sm:col-span-2 p-4 bg-slate-50 rounded-xl border border-slate-100" data-testid="company-benefits-view">
                          <p className="text-xs text-slate-400 mb-2">{t("company.benefits")}</p>
                          <ul className="space-y-1">
                            {company.benefits.filter(Boolean).map((b, i) => (
                              <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                                <CheckCircle className="h-3.5 w-3.5 text-[#028CF8] mt-0.5 flex-shrink-0" /> {b}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Plan y Facturación — 100% real data from GET /company/access.
                    Paid plans go through Stripe Checkout/webhook (Sprint 4); free
                    plans/trials/manual admin grants keep working exactly as before. ── */}
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm mt-6" data-testid="billing-section">
                  <div className="flex items-center gap-2 mb-5">
                    <CreditCard className="h-5 w-5 text-[#028CF8]" />
                    <h3 className="font-poppins text-lg font-bold text-slate-900">{t("companyBilling.title")}</h3>
                  </div>

                  {accessStatus?.has_access && accessStatus.access ? (
                    <div className="space-y-4">
                      {(accessStatus.access.subscription_status === "past_due" || accessStatus.access.subscription_status === "unpaid") && (
                        <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="billing-past-due-warning">
                          <p className="text-sm text-amber-800 font-medium">{t("companyBilling.pastDueWarning")}</p>
                          <button onClick={openBillingPortal} disabled={portalLoading} data-testid="billing-past-due-cta"
                            className="h-9 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-all disabled:opacity-50">
                            {t("companyBilling.pastDueCta")}
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-poppins font-bold text-slate-900 text-base" data-testid="billing-plan-name">{accessStatus.access.plan_name}</p>
                          {(() => {
                            const access = accessStatus.access;
                            const status = access.subscription_status;
                            let label = access.source === "trial" ? t("companyBilling.trialActive") : t("companyBilling.planActive");
                            let cls = access.source === "trial" ? "bg-[#F55FA5]/10 text-[#F55FA5]" : "bg-green-50 text-green-700";
                            if (status === "past_due" || status === "unpaid") { label = t("companyBilling.statusPastDue"); cls = "bg-amber-50 text-amber-700"; }
                            else if (access.cancel_at_period_end) { label = t("companyBilling.statusCancelling"); cls = "bg-orange-50 text-orange-700"; }
                            return <span className={`inline-flex items-center gap-1.5 mt-1 text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`} data-testid="billing-status-badge">{label}</span>;
                          })()}
                        </div>
                        <div className="flex items-center gap-2">
                          {accessStatus.access.source === "stripe" && (
                            <button onClick={openBillingPortal} disabled={portalLoading} data-testid="billing-manage-button"
                              className="h-10 px-5 rounded-xl border-2 border-[#028CF8] text-[#028CF8] hover:bg-[#028CF8]/5 text-sm font-semibold transition-all disabled:opacity-50">
                              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("companyBilling.manageBilling")}
                            </button>
                          )}
                          <button onClick={openAccessModal} data-testid="billing-change-plan-button"
                            className="h-10 px-5 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all">
                            {t("companyBilling.changePlan")}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {accessStatus.access.terms_snapshot?.price_amount != null && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">{t("companyBilling.price")}</p>
                            <p className="font-semibold text-slate-900 text-sm">
                              {accessStatus.access.terms_snapshot.price_currency} {accessStatus.access.terms_snapshot.price_amount}
                            </p>
                          </div>
                        )}
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <p className="text-xs text-slate-400 mb-1">{accessStatus.access.cancel_at_period_end ? t("companyBilling.accessUntil") : t("companyBilling.expiresAt")}</p>
                          <p className="font-semibold text-slate-900 text-sm" data-testid="billing-expires-at">
                            {accessStatus.access.expires_at ? new Date(accessStatus.access.expires_at).toLocaleDateString() : t("companyBilling.noExpiry")}
                          </p>
                        </div>
                        {accessStatus.listings_limit != null ? (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">{t("companyBilling.listings")}</p>
                            <p className="font-semibold text-slate-900 text-sm" data-testid="billing-listings-usage">
                              {accessStatus.listings_used}/{accessStatus.listings_limit} {t("companyAccess.listingsIncluded")}
                            </p>
                          </div>
                        ) : (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <p className="text-xs text-slate-400 mb-1">{t("companyBilling.listings")}</p>
                            <p className="font-semibold text-slate-900 text-sm">{t("companyBilling.unlimited")}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="billing-no-plan">
                      <p className="text-sm text-slate-500">{t("companyBilling.noActivePlan")}</p>
                      <button onClick={openAccessModal} data-testid="billing-change-plan-button"
                        className="h-10 px-5 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all">
                        {t("companyBilling.changePlan")}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <PrivacySettingsPanel />
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Mobile bottom navigation is now owned by the global <BottomNav/>
          (App.js) via AppShellContext — no page-level nav here anymore. */}

      {/* ── Access gate — full mobile screen (not a floating error popup); a normal
          centered dialog again from sm: up. Plans/trial data are 100% real, from
          GET /plans/public + GET /company/access — nothing hardcoded. ── */}
      <Dialog open={showAccessModal} onOpenChange={setShowAccessModal}>
        <DialogContent
          data-testid="access-gate-modal"
          className="w-full h-[100dvh] max-w-full rounded-none p-0 gap-0 top-0 left-0 translate-x-0 translate-y-0 flex flex-col overflow-hidden sm:h-auto sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl sm:p-0 sm:gap-0 sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2"
        >
          <div className="px-5 pt-6 pb-4 border-b border-slate-100 flex-shrink-0">
            <DialogHeader>
              <DialogTitle data-testid="access-gate-title" className="font-poppins text-xl text-slate-900">
                {accessGateReason === "listing_limit_reached" ? t("companyAccess.limitTitle") : t("companyAccess.title")}
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                {accessGateReason === "listing_limit_reached" ? t("companyAccess.limitSubtitle") : t("companyAccess.subtitle")}
              </DialogDescription>
            </DialogHeader>
            {accessGateReason === "listing_limit_reached" && accessStatus?.listings_limit != null && (
              <div className="bg-[#028CF8]/5 border border-[#028CF8]/20 rounded-xl p-3 flex items-center justify-between text-sm mt-3" data-testid="listings-quota-summary">
                <span className="text-slate-600 font-medium">{accessStatus.access?.plan_name}</span>
                <span className="font-poppins font-bold text-[#028CF8]">{accessStatus.listings_used}/{accessStatus.listings_limit} {t("companyAccess.listingsIncluded")}</span>
              </div>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4" style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom))" }}>
            <div className="space-y-3">
              {accessStatus?.access?.source === "stripe" && accessStatus.access?.stripe_subscription_id ? (
                <div className="text-center py-6" data-testid="already-subscribed-note">
                  <p className="text-sm text-slate-600 mb-4">{t("companyAccess.alreadySubscribedNote")}</p>
                  <button type="button" onClick={openBillingPortal} disabled={portalLoading} data-testid="access-modal-manage-billing-button"
                    className="w-full h-11 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold disabled:opacity-50">
                    {portalLoading ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : t("companyBilling.manageBilling")}
                  </button>
                </div>
              ) : publicPlans === null ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" /></div>
              ) : publicPlans.length === 0 ? (
                <p className="text-sm text-slate-600 text-center py-8" data-testid="no-plans-available">{t("companyAccess.noPlansAvailable")}</p>
              ) : (
                publicPlans.map((plan) => {
                  const canTrial = plan.trial_enabled && plan.trial_days && !accessStatus?.trial_used;
                  const isBusy = activatingPlanId === plan.plan_id;
                  const isPaid = !!plan.price_amount;
                  return (
                    <div key={plan.plan_id} data-testid={`access-plan-${plan.plan_id}`}
                      className="border-2 border-slate-200 rounded-2xl p-4 hover:border-[#028CF8]/40 transition-colors">
                      <p className="font-poppins font-bold text-slate-900 text-base">{plan.name}</p>
                      {plan.price_amount != null && (
                        <p className="text-lg text-[#028CF8] font-poppins font-bold mt-0.5">
                          {plan.price_currency} {plan.price_amount}
                          <span className="text-sm text-slate-500 font-medium"> / {t(`companyAccess.durationUnit.${plan.duration_unit}`)}</span>
                        </p>
                      )}
                      {plan.listings_mode === "custom" && plan.listings_amount != null && (
                        <p className="text-sm text-slate-600 mt-1">{plan.listings_amount} {t("companyAccess.listingsIncluded")}</p>
                      )}
                      {plan.benefits?.length > 0 && (
                        <ul className="mt-3 space-y-1.5">
                          {plan.benefits.map((b, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-[#028CF8] flex-shrink-0" /> {b}</li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-col gap-2 mt-4">
                        <button
                          type="button"
                          data-testid={`activate-plan-${plan.plan_id}`}
                          onClick={() => (isPaid ? checkoutForPlan(plan.plan_id) : activatePlan(plan.plan_id))}
                          disabled={isBusy}
                          className="w-full flex items-center justify-center gap-1.5 h-11 px-4 rounded-xl bg-[#028CF8] hover:bg-[#0270C5] text-white text-sm font-semibold transition-all disabled:opacity-50"
                        >
                          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          {isPaid ? t("companyAccess.continueToPayment") : t("companyAccess.choosePlan")}
                        </button>
                        {canTrial && (
                          <button
                            type="button"
                            data-testid={`start-trial-${plan.plan_id}`}
                            onClick={() => startTrial(plan.plan_id)}
                            disabled={isBusy}
                            className="w-full flex items-center justify-center gap-1.5 h-11 px-4 rounded-xl border-2 border-[#F55FA5] text-[#F55FA5] text-sm font-semibold hover:bg-[#F55FA5]/5 transition-all disabled:opacity-50"
                          >
                            {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {t("companyAccess.startTrial")} ({plan.trial_days} {t("companyAccess.days")})
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <p className="text-xs text-slate-500 text-center pt-1" data-testid="access-gate-form-preserved-note">{t("companyAccess.formPreservedNote")}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {viewingCv && (
        <CVPdfViewerModal
          title={viewingCv.name}
          subtitle={viewingCv.job_title}
          fetchPath={viewingCv.cv_type ? `/applications/${viewingCv.application_id}/cv-pdf` : `/applications/${viewingCv.application_id}/cv`}
          downloadFilename={`${(viewingCv.name || "CV").replace(/\s+/g, "_")}_CV.pdf`}
          onClose={() => setViewingCv(null)}
        />
      )}

      {viewingCandidate && (
        <EmployerCandidateView
          app={viewingCandidate}
          autoOpenComposer={!!viewingCandidate.__openComposer}
          onClose={() => setViewingCandidate(null)}
          onViewCv={setViewingCv}
          t={t}
        />
      )}

      {notifyCtx && (
        <NotifyApplicantModal
          application={notifyCtx.application}
          stage={notifyCtx.stage}
          onClose={() => setNotifyCtx(null)}
          onSent={() => showToast(t("notifyApplicant.sentToast"))}
        />
      )}

      {interviewModalCtx && (
        <InterviewFormModal
          application={interviewModalCtx.application}
          initialInterview={interviewModalCtx.initialInterview}
          defaultContactName={user?.name}
          onClose={() => setInterviewModalCtx(null)}
          onSaved={(notification) => {
            showToast(interviewModalCtx.initialInterview ? t("company.saveInterviewChanges") : t("company.scheduleInterviewBtn"));
            const interviewData = notification?.metadata?.interview;
            setApplications((prev) => prev.map((a) => (
              a.application_id === interviewModalCtx.application.application_id
                ? { ...a, stage: "interview", interview_details: interviewData || a.interview_details }
                : a
            )));
          }}
        />
      )}

      {offerModalCtx && (
        <OfferFormModal
          application={offerModalCtx.application}
          job={offerModalCtx.job}
          onClose={() => setOfferModalCtx(null)}
          onSent={(offer) => {
            showToast(t("offerModal.sentToast"));
            setApplications((prev) => prev.map((a) => (
              a.application_id === offerModalCtx.application.application_id
                ? { ...a, stage: "offer", offer }
                : a
            )));
          }}
        />
      )}
    </div>
  );
}
